import React, { createContext, useContext, useMemo, useState } from 'react';

export const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'hi', label: 'हिन्दी', short: 'HI' },
  { code: 'ta', label: 'தமிழ்', short: 'TA' },
  { code: 'kn', label: 'ಕನ್ನಡ', short: 'KN' },
];

const COPY = {
  en: {
    createInvoice: 'Create invoice',
    dashboard: 'Dashboard',
    history: 'History',
    sales: 'Sales',
    salesReports: 'Sales reports',
    reports: 'Reports',
    customers: 'Customers',
    items: 'Items',
    profile: 'Profile',
    adminPanel: 'Admin panel',
    logout: 'Logout',
    login: 'Login',
    guest: 'Guest',
    notLoggedIn: 'Not logged in',
    language: 'Language',
    continueWithGoogle: 'Continue with Google',
    loginIntro: 'Sign in or create your BillJaldi account with Google. No password needed. Your invoice history is saved automatically after login.',
    loadingGoogle: 'Loading Google sign-in...',
    googleUnavailable: 'Google sign-in is unavailable right now.',
    verifyingGoogle: 'Verifying Google and setting up your workspace',
    welcome: 'Welcome',
    openingWorkspace: 'Welcome. Opening your workspace...',
    starterDetail: '100 invoices/month after upgrade',
    createGstInvoice: 'Create a GST invoice',
    livePreview: 'Live Preview',
    fullScreenPreview: 'Full screen preview',
    closePreview: 'Close preview',
    limitReached: 'Monthly invoice limit reached',
    upgradePlan: 'Upgrade plan',
    viewHistory: 'View history',
    settings: 'Settings',
    settingsIntro: 'Save GSTIN, state, and business details for invoice autofill.',
    accountProfile: 'Account Profile',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    saveProfile: 'Save profile',
    legalConsent: 'By using this application, you consent to BillJaldi legal terms and data use.',
    legalTerms: 'Legal terms',
    yourBusiness: 'Your Business',
    businessName: 'Business Name',
    gstin: 'GSTIN',
    logo: 'Logo',
    uploadLogo: 'Upload logo',
    remove: 'Remove',
    state: 'State',
    phone: 'Phone',
    address: 'Address',
    sealSignature: 'Seal and Signature',
    sealSignatureIntro: 'Optional. Upload a combined business seal/signature image and BillJaldi will place it on every invoice.',
    uploadSealSignature: 'Upload seal/signature',
    adminOverview: 'Admin overview',
    adminIntro: 'Users, plan payments, and customer billing volume.',
    searchUsers: 'Search users...',
    users: 'Users',
    paidUsers: 'Paid users',
    planRevenue: 'Plan revenue',
    invoiceVolume: 'Invoice volume',
    invoices: 'Invoices',
    user: 'User',
    plan: 'Plan',
    subscription: 'Subscription',
    planControl: 'Plan control',
    business: 'Business',
    joined: 'Joined',
    apply: 'Apply',
    clear: 'Clear',
    reasonNote: 'Reason / internal note',
    loading: 'Loading...',
    billTo: 'Bill To',
    pickSaved: 'Pick saved',
    search: 'Search...',
    noCustomersSaved: 'No customers saved yet',
    clearCustomer: 'Clear',
    customerName: 'Customer Name',
    gstinOptional: 'GSTIN (optional)',
    placeOfSupply: 'State (Place of supply)',
    emailOptional: 'Email (optional)',
    phoneWhatsapp: 'Phone / WhatsApp',
    saveCustomerLater: 'Save customer for later',
    invoiceDetailsPayment: 'Invoice Details & Payment',
    invoiceNumber: 'Invoice #',
    date: 'Date',
    dueDateOptional: 'Due Date (optional)',
    paymentTerms: 'Payment Terms',
    customTerms: 'Custom Terms',
    bankName: 'Bank Name',
    accountNumber: 'Account Number',
    ifsc: 'IFSC',
    upiId: 'UPI ID',
    notesTerms: 'Notes / Terms',
    taxSummary: 'Tax Summary',
    subtotal: 'Subtotal',
    discount: 'Discount',
    taxableAmount: 'Taxable Amount',
    grandTotal: 'Grand Total',
    amountInWords: 'Amount in words',
    downloadPdf: 'Download PDF',
    generating: 'Generating...',
    newInvoice: 'New Invoice',
    share: 'Share',
    preparing: 'Preparing...',
    itemsServices: 'Items & Services',
    delete: 'Delete',
    lineItems: 'Line items',
    description: 'Description',
    qty: 'Qty',
    rate: 'Rate',
    disc: 'Disc%',
    gst: 'GST',
    amount: 'Amount',
    duplicate: 'Duplicate',
    addRow: 'Add row',
    addFiveRows: 'Add 5 rows',
    accountInvoicesSaved: 'Your account invoices are saved securely.',
    loginToSaveHistory: 'Log in to save and view invoice history.',
    all: 'All',
    selected: 'selected',
    download: 'Download',
    noInvoicesYet: 'No invoices yet',
    noHistoryWithoutLogin: 'No history without login',
    createFirstInvoice: 'Create your first invoice',
    loginToSaveInvoices: 'Log in to save invoices',
    invoiceHash: 'Invoice #',
    customer: 'Customer',
    status: 'Status',
    dueDate: 'Due date',
    preview: 'Preview',
    generatingPreview: 'Generating preview...',
    add: 'Add',
    addCustomer: 'Add customer',
    addItem: 'Add item',
    editItem: 'Edit item',
    update: 'Update',
    cancel: 'Cancel',
    template: 'Template',
    csv: 'CSV',
    name: 'Name',
    selectState: 'Select state',
    noCustomersYet: 'No customers yet',
    lastTenBrowser: 'Last 10 saved on this browser.',
    customersSource: 'Customers from invoice history and CSV uploads.',
    itemsSource: 'Items from invoice history and CSV uploads.',
    noItemsYet: 'No items yet',
    unit: 'Unit',
    purchasePrice: 'Purchase Price',
    salePrice: 'Sale Price',
    purchase: 'Purchase',
    sale: 'Sale',
    confirmingPayment: 'Confirming your payment...',
    hangTight: 'Hang tight. This usually takes 2-5 seconds.',
    planActivated: 'Plan activated',
    planActiveCopy: 'Your BillJaldi plan is active. You can keep creating invoices now.',
    startCreatingInvoices: 'Start creating invoices',
    sessionExpired: 'Session expired',
    paymentNotConfirmed: 'Payment not confirmed',
    paymentTimeoutCopy: "We couldn't confirm within the expected time. Please check your email or try again.",
    paymentRetryCopy: 'Please try the checkout again from the plan page.',
    backToPlan: 'Back to plan',
    shareInvoice: 'Share invoice',
    emailWord: 'Email',
    nativeShareUnavailable: 'Native PDF sharing is not available in this browser.',
    pdfDownloadedAttach: 'PDF downloaded. Attach it after the window opens.',
    pdfShareFailed: 'Could not prepare PDF for sharing.',
    markSentTitle: 'Mark this invoice as sent?',
    markSentBody: 'Sharing marks this invoice as sent and locks invoice edits. You can still mark it paid or cancelled later, but invoice contents will not be editable after this.',
    keepEditing: 'Keep editing',
    keepAsDraft: 'Keep as draft',
    confirmAndShare: 'Confirm and share',
    clientPlaceholder: 'Client full name or company',
    streetPlaceholder: 'Street, City, PIN',
    customTermsPlaceholder: 'Due on receipt, advance payment, milestone terms...',
    notesPlaceholder: 'Payment terms, thank-you note, or any reference.',
    markSentActionBody: 'Sharing marks this invoice as sent and locks invoice edits. You can still mark it paid or cancelled from History, but the invoice contents will not be editable after this.',
    draft: 'Draft',
    sent: 'Sent',
    paid: 'Paid',
    overdue: 'Overdue',
    cancelled: 'Cancelled',
    selectAllInvoices: 'Select all visible invoices',
    selectInvoice: 'Select invoice',
    downloaded: 'Downloaded',
    invoiceSingular: 'invoice',
    invoicePlural: 'invoices',
    deleted: 'Deleted',
    statusUpdateFailed: 'Status update failed',
    marked: 'Marked',
    couldNotMarkSent: 'Could not mark invoice sent',
    required: 'is required',
    saved: 'Saved',
    savedLocally: 'Saved locally',
    failedToSave: 'Failed to save',
    noCustomersFoundCsv: 'No customers found in CSV',
    noItemsFoundCsv: 'No items found in CSV',
    imported: 'Imported',
    locally: 'locally',
    saveInvoicesForever: 'Save invoices forever.',
    invoiceHistory: 'Invoice history',
    searchInvoices: 'Search by invoice # or customer...',
    newestFirst: 'Newest first',
    oldestFirst: 'Oldest first',
    welcomeLine: 'Welcome,',
    dashboardHistoryBasis: 'Your numbers are based on invoice history.',
    intelligenceLayer: 'Intelligence layer',
    moneyDashboard: 'Money dashboard',
    starterSummary: 'Starter summary',
    basicLockedCopy: 'Basic turns saved invoices into collections, GST, sales, profit, and ledger intelligence.',
    basicPreviewCopy: 'Upgrade to turn saved invoices into collections, GST, sales, profit, and ledger intelligence.',
    turnInsightsOn: 'Turn insights on',
    overview: 'Overview',
    collections: 'Collections',
    gstView: 'GST',
    profit: 'Profit',
    ledger: 'Ledger',
    payments: 'Payments',
    followups: 'Follow-ups',
    exports: 'Exports',
    salesWorkspaceCopy: 'Customer-wise receivables, payment queue, and follow-ups. Invoice actions stay in History.',
    salesReportsCopy: 'Customer ledger, payment tracking, follow-ups, and exports from saved invoice history.',
    paymentQueue: 'Payment queue',
    paymentQueueCopy: 'Sent, overdue, and paid invoices grouped for collection tracking.',
    openInHistory: 'Open in History',
    noFollowups: 'No overdue follow-ups right now.',
    billedThisMonth: 'Billed this month',
    collectedThisMonth: 'Collected this month',
    outstanding: 'Outstanding',
    estimatedGst: 'Estimated GST',
    estimatedProfit: 'Estimated profit',
    grossMargin: 'Gross margin',
    averageInvoiceValue: 'Average invoice value',
    needsFollowup: 'Needs follow-up',
    agingBuckets: 'Aging buckets',
    topCustomers: 'Top customers',
    topItems: 'Top items/services',
    revenueTrend: 'Revenue trend',
    customerLedger: 'Customer ledger',
    totalBilled: 'Total billed',
    lastInvoice: 'Last invoice',
    paidAmount: 'Paid amount',
    unpaidAmount: 'Unpaid amount',
    paymentMethod: 'Payment method',
    paymentDate: 'Payment date',
    reportCenter: 'Report center',
    reportCenterCopy: 'Exports for GST, sales review, customer follow-up, and CA cleanup.',
    gstSummaryExport: 'GST summary export',
    salesCsvExport: 'Sales CSV export',
    customerOutstandingExport: 'Customer outstanding CSV',
    profitSummary: 'Profit summary',
    export: 'Export',
    accountInvoices: 'Account invoices',
    accountInvoicesHint: 'Saved invoices in this account against the current monthly plan limit.',
    invoicesSent: 'Invoices sent',
    invoicesSentHint: 'Invoices marked Sent or Overdue. Drafts are excluded because they have not gone out yet.',
    accountsReceivable: 'Accounts receivable',
    accountsReceivableHint: 'Money still expected from customers. This includes Sent and Overdue invoices, not Drafts or Cancelled invoices.',
    accountsReceived: 'Accounts received',
    accountsReceivedHint: 'Money from invoices marked Paid. Change invoice status in History to keep this accurate.',
    collectionSnapshot: 'Collection snapshot',
    collectionSnapshotCopy: 'Paid, pending, and overdue money from invoice activity.',
    collectionSnapshotHint: 'Red includes overdue sent invoices plus past-due drafts that need review before sending.',
    details: 'details',
    collectionRate: 'Collection rate',
    invoiceValue: 'Invoice value',
    drafts: 'Drafts',
    pastDue: 'past due',
    received: 'Received',
    pending: 'Pending',
    needsAttention: 'Overdue / needs attention',
    unlimited: 'unlimited',
    monthly: 'Monthly',
    yearly: 'Yearly',
    savePercent: 'Save 17%',
    billedMonthly: 'Billed monthly',
    billedYearly: 'Billed yearly',
    cancelAnytime: 'Cancel anytime',
    launchPrice: 'Launch price',
    regularPrice: 'regular price',
    launchFirst50: 'First 50 users only',
    starter: 'Starter',
    basic: 'Basic',
    chooseStarter: 'Choose Starter',
    chooseBasic: 'Choose Basic',
    openingRazorpay: 'Opening Razorpay...',
    checkoutFailed: 'Checkout could not be started. Please try again.',
    starterActive: 'Starter is already active.',
    basicActive: 'Basic is already active.',
    activated: 'activated',
    manualWhatsappShare: 'Manual WhatsApp share',
    accountInvoiceHistory: 'Account invoice history',
    businessProfileAutofill: 'Business profile and GST autofill',
    recurringMandate: 'Recurring mandate until cancelled',
    basicIntelligence: 'Dashboard intelligence and sales reports',
    unlimitedInvoices: 'Unlimited invoices',
    comingSoon: 'Coming soon',
    whatLaunchesNext: 'What launches next',
    julyOnward: 'July onward',
    basicPlanSoon: 'Basic plan',
    basicPlanSoonCopy: 'Unlimited invoices, ledger, GST estimator, profit calculator, outstanding tracker, and CA exports.',
    razorpayIntegration: 'Razorpay integration',
    razorpayIntegrationCopy: 'Import payments, match invoices, and track paid, unpaid, and partially paid bills.',
    metaIntegration: 'Meta integration',
    metaIntegrationCopy: 'Connect Meta commerce and ad workflows for seller billing and reconciliation.',
    shopifyImport: 'Shopify import',
    shopifyImportCopy: 'Import orders and generate GST invoices from store sales.',
    whatsappAutomation: 'WhatsApp automation',
    whatsappAutomationCopy: 'Send invoice links, due-date nudges, and payment reminders over WhatsApp.',
    codRtoTracker: 'COD/RTO tracker',
    codRtoTrackerCopy: 'Track cash-on-delivery, failed delivery, and return-to-origin adjustments.',
    returnsRefunds: 'Returns and refunds',
    returnsRefundsCopy: 'Handle return, refund, and credit-note adjustments without breaking invoice history.',
    sevenYearStorage: '7-year storage',
    sevenYearStorageCopy: 'Long-term invoice and document retention for tax records.',
    accountEmail: 'Account email',
    businessEmail: 'Business email',
    profileSaved: 'Profile saved',
    profileSaveFailed: 'Profile save failed',
    savedOnBrowser: 'Saved on this browser',
    sealSignatureSizeError: 'Seal/signature image must be under 2MB',
    sealSignatureAlt: 'Seal and signature',
    sealSignatureHelp: 'Use a transparent PNG if possible. The image appears near the authorised signature area in invoice previews and downloaded PDFs.',
    wantCloudSync: 'Want cloud sync?',
    cloudSyncCopy: 'Upgrade to Starter for more monthly invoices and saved business details.',
    viewPlan: 'View plan',
    superadmin: 'Superadmin',
    permanent: 'Permanent',
    sevenDays: '7 days',
    fourteenDays: '14 days',
    thirtyDays: '30 days',
    ninetyDays: '90 days',
    superadminPlanOverride: 'Superadmin plan override',
    planOverrideApplied: 'Plan override applied',
    planOverrideCleared: 'Plan override cleared',
    saveInvoicesAccount: 'Save invoices to your account',
    saveInvoicesAccountCopy: 'Free accounts include 5 watermarked invoices a month with history. Starter gives you 100 invoices and saved business details.',
    invoicesSavedAfterLogin: 'Your invoices are saved after login',
    invoicesSavedAfterLoginCopy: 'Create a free account for invoice history. Starter adds more monthly invoices and GST profile autofill.',
    upgradeInvoiceLimit: 'Upgrade your invoice limit',
    upgradeInvoiceLimitCopy: 'Starter gives you 100 monthly invoices, account history, and saved GST business details.',
    invoicesEverywhere: 'Your invoices, everywhere you work',
    invoicesEverywhereCopy: 'Starter gives you more monthly invoices, account history, and saved GST business details.',
    cloudSync: 'Cloud Sync',
    invoiceHistoryFeature: 'Invoice History',
    hundredInvoices: '100 Invoices',
    notNow: 'Not now',
    seePlan: 'See plan',
    homeHeroKicker: 'GST invoices for Indian sellers',
    homeHeroTitle: 'Stop fighting spreadsheets for every GST invoice.',
    homeHeroCopy: 'BillJaldi helps you create clean GST invoices, share them on WhatsApp, and keep customer records ready when your buyer or CA asks.',
    startFree: 'Start free',
    painTitle: 'The messy part of billing is not the PDF.',
    painCopy: 'It is remembering invoice numbers, splitting GST correctly, finding old customer details, and proving what was sent when money is due.',
    painOldInvoices: 'Old invoices disappear across chats and downloads.',
    painTaxSplit: 'CGST, SGST, and IGST checks slow you down.',
    painFollowup: 'Payment follow-ups happen manually on WhatsApp.',
    painRecords: 'CA-ready records are scattered at month end.',
    howHelps: 'How BillJaldi helps',
    helpFastPdf: 'Create GST invoice PDFs in seconds',
    helpAutoTax: 'Auto-calculate CGST, SGST, and IGST',
    helpShare: 'Share by WhatsApp or email with due date text',
    helpHistory: 'Save invoice history after login',
    helpCustomersItems: 'Reuse saved customers and items',
    helpSeal: 'Add seal and signature from profile',
    builtFor: 'Built for',
    builtFreelancers: 'Freelancers',
    builtShops: 'Small shops',
    builtInstagram: 'Instagram sellers',
    builtServices: 'Service providers',
    builtD2c: 'D2C operators',
    homePlanCopy: 'Free gives 5 invoices/month. Starter is ₹249/month. Regular price ₹399.',
    legal: 'Legal',
    privacy: 'Privacy',
    changelog: 'Changelog',
    home: 'Home',
    versionHistory: 'Version history',
    versionHistoryCopy: 'A simple record of what shipped and what is coming next.',
    currentVersion: 'Current version',
    upcoming: 'Upcoming',
    upcomingCopy: 'Planned product tracks after v1.1.',
    ledgerFeature: 'Ledger',
    ledgerFeatureCopy: 'Customer-wise receivables, paid/unpaid status, and account-level views.',
    v10Title: 'v1.0 public launch',
    v10Copy: 'Google sign-in, invoice creation, PDF download, saved history, customers, items, dashboard, Starter payments, and admin controls.',
    v11Title: 'v1.1 in progress',
    v11Copy: 'Language support, new landing page, changelog, seal and stamp support, and better mobile navigation.',
    hidePreview: 'Hide',
    invoiceLimitCopy: 'You have reached this month’s invoice limit. Upgrade to create more invoices this month.',
    loggedOut: 'Logged out',
    signingOut: 'Signing you out',
    logoutDoneCopy: 'This device is clean. Invoice drafts, local history, customers, and items were cleared.',
    logoutProgressCopy: 'Clearing this device and closing your workspace securely.',
    builtMadeIndia: 'Built and made in India',
    upgrade: 'Upgrade',
    free: 'Free',
    expandSidebar: 'Expand sidebar',
    collapseSidebar: 'Collapse sidebar',
    businessProfile: 'Business profile',
    previewFailed: 'Preview failed',
    duplicateLimitReached: 'You have used this month’s invoice limit. Upgrade to duplicate.',
    invoiceCopied: 'Invoice copied. Review and download when ready.',
    pdfCloudStorageFailed: 'Invoice saved, but PDF cloud storage failed.',
    watermarkNotice: 'Free and Starter invoices include a BillJaldi watermark.',
    loginSaveHistoryNotice: 'Log in to save invoice history.',
    invoiceDownloaded: 'Invoice downloaded!',
    invoiceSaved: 'Invoice saved.',
    saveInvoice: 'Save invoice',
    saving: 'Saving...',
    downloadAndShare: 'Download and share',
    pdfGenerateFailed: 'Failed to generate PDF. Please try again.',
    invoiceDisclaimer: 'billjaldi is not liable for the accuracy, legality, or tax compliance of any invoice generated.',
    logoSizeError: 'Logo must be under 2MB',
    loginRightHeadline: 'Your invoices, customers, and items stay tied to your account.',
    loginRightCopy: 'Once you sign in, BillJaldi saves account invoices automatically. No manual cloud migration, no separate password, no duplicate signup path.',
    hundredInvoicesMonth: '100 invoices/month',
    help: 'Help',
    helpPageTitle: 'Tell us what is stuck',
    helpPageIntro: 'Send a support request from inside your BillJaldi account. We keep the request tied to your login so follow-up stays clean.',
    queryTitle: 'Query title',
    queryBody: 'Query details',
    queryTitlePlaceholder: 'Example: Invoice PDF is not downloading',
    queryBodyPlaceholder: 'Tell us what happened, what you expected, and any invoice number if relevant.',
    helpPrivateCopy: 'Your account details are attached automatically. The support inbox stays private.',
    submitQuery: 'Submit query',
    sending: 'Sending...',
    helpSubmitted: 'Help request submitted.',
    helpSubmitFailed: 'Help request could not be submitted.',
    supportQueue: 'Support queue',
    supportQueueCopy: 'Open help requests from logged-in users.',
    supportStatus: 'Support status',
    emailStatus: 'Email status',
    resolved: 'Resolved',
    open: 'Open',
    resolve: 'Resolve',
    reopen: 'Reopen',
    noSupportTickets: 'No support requests yet.',
  },
  hi: {
    createInvoice: 'इनवॉइस बनाएं',
    dashboard: 'डैशबोर्ड',
    history: 'इतिहास',
    customers: 'ग्राहक',
    items: 'आइटम',
    profile: 'प्रोफाइल',
    adminPanel: 'एडमिन पैनल',
    logout: 'लॉगआउट',
    login: 'लॉगिन',
    guest: 'अतिथि',
    notLoggedIn: 'लॉगिन नहीं',
    language: 'भाषा',
    continueWithGoogle: 'Google से जारी रखें',
    loginIntro: 'Google से साइन इन करें या BillJaldi खाता बनाएं। पासवर्ड की जरूरत नहीं। आपकी इनवॉइस हिस्ट्री लॉगिन के बाद अपने आप सेव होती है।',
    loadingGoogle: 'Google साइन-इन लोड हो रहा है...',
    googleUnavailable: 'Google साइन-इन अभी उपलब्ध नहीं है।',
    verifyingGoogle: 'Google सत्यापित हो रहा है और आपका वर्कस्पेस तैयार हो रहा है',
    welcome: 'स्वागत है',
    openingWorkspace: 'स्वागत है। आपका वर्कस्पेस खुल रहा है...',
    starterDetail: 'अपग्रेड के बाद 100 इनवॉइस/माह',
    createGstInvoice: 'GST इनवॉइस बनाएं',
    livePreview: 'लाइव प्रीव्यू',
    fullScreenPreview: 'फुल स्क्रीन प्रीव्यू',
    closePreview: 'प्रीव्यू बंद करें',
    limitReached: 'मासिक इनवॉइस सीमा पूरी हो गई',
    upgradePlan: 'प्लान अपग्रेड करें',
    viewHistory: 'इतिहास देखें',
    settings: 'सेटिंग्स',
    settingsIntro: 'इनवॉइस ऑटोफिल के लिए GSTIN, राज्य और बिजनेस विवरण सेव करें।',
    accountProfile: 'अकाउंट प्रोफाइल',
    firstName: 'पहला नाम',
    lastName: 'अंतिम नाम',
    email: 'ईमेल',
    saveProfile: 'प्रोफाइल सेव करें',
    legalConsent: 'इस ऐप का उपयोग करके आप BillJaldi की कानूनी शर्तों और डेटा उपयोग से सहमत हैं।',
    legalTerms: 'कानूनी शर्तें',
    yourBusiness: 'आपका बिजनेस',
    businessName: 'बिजनेस नाम',
    gstin: 'GSTIN',
    logo: 'लोगो',
    uploadLogo: 'लोगो अपलोड करें',
    remove: 'हटाएं',
    state: 'राज्य',
    phone: 'फोन',
    address: 'पता',
    sealSignature: 'सील और हस्ताक्षर',
    sealSignatureIntro: 'वैकल्पिक। संयुक्त सील/हस्ताक्षर इमेज अपलोड करें और BillJaldi इसे हर इनवॉइस पर लगाएगा।',
    uploadSealSignature: 'सील/हस्ताक्षर अपलोड करें',
    adminOverview: 'एडमिन ओवरव्यू',
    adminIntro: 'यूजर, प्लान पेमेंट और ग्राहक बिलिंग वॉल्यूम।',
    searchUsers: 'यूजर खोजें...',
    users: 'यूजर',
    paidUsers: 'पेड यूजर',
    planRevenue: 'प्लान रेवेन्यू',
    invoiceVolume: 'इनवॉइस वॉल्यूम',
    invoices: 'इनवॉइस',
    user: 'यूजर',
    plan: 'प्लान',
    subscription: 'सब्सक्रिप्शन',
    planControl: 'प्लान कंट्रोल',
    business: 'बिजनेस',
    joined: 'जॉइन किया',
    apply: 'लागू करें',
    clear: 'क्लियर',
    reasonNote: 'कारण / आंतरिक नोट',
    loading: 'लोड हो रहा है...',
    billTo: 'बिल टू',
    pickSaved: 'सेव ग्राहक चुनें',
    search: 'खोजें...',
    noCustomersSaved: 'अभी कोई ग्राहक सेव नहीं',
    clearCustomer: 'क्लियर',
    customerName: 'ग्राहक नाम',
    gstinOptional: 'GSTIN (वैकल्पिक)',
    placeOfSupply: 'राज्य (सप्लाई स्थान)',
    emailOptional: 'ईमेल (वैकल्पिक)',
    phoneWhatsapp: 'फोन / WhatsApp',
    saveCustomerLater: 'ग्राहक बाद के लिए सेव करें',
    invoiceDetailsPayment: 'इनवॉइस विवरण और भुगतान',
    invoiceNumber: 'इनवॉइस #',
    date: 'तारीख',
    dueDateOptional: 'देय तारीख (वैकल्पिक)',
    paymentTerms: 'भुगतान शर्तें',
    customTerms: 'कस्टम शर्तें',
    bankName: 'बैंक नाम',
    accountNumber: 'खाता नंबर',
    ifsc: 'IFSC',
    upiId: 'UPI ID',
    notesTerms: 'नोट्स / शर्तें',
    taxSummary: 'टैक्स सारांश',
    subtotal: 'सबटोटल',
    discount: 'डिस्काउंट',
    taxableAmount: 'टैक्स योग्य राशि',
    grandTotal: 'कुल राशि',
    amountInWords: 'राशि शब्दों में',
    downloadPdf: 'PDF डाउनलोड करें',
    generating: 'बन रहा है...',
    newInvoice: 'नई इनवॉइस',
    share: 'शेयर',
    preparing: 'तैयार हो रहा है...',
    itemsServices: 'आइटम और सेवाएं',
    delete: 'डिलीट',
    lineItems: 'लाइन आइटम',
    description: 'विवरण',
    qty: 'मात्रा',
    rate: 'रेट',
    disc: 'छूट%',
    gst: 'GST',
    amount: 'राशि',
    duplicate: 'डुप्लिकेट',
    addRow: 'रो जोड़ें',
    addFiveRows: '5 रो जोड़ें',
    accountInvoicesSaved: 'आपके अकाउंट इनवॉइस सुरक्षित रूप से सेव हैं।',
    loginToSaveHistory: 'इनवॉइस इतिहास सेव और देखने के लिए लॉगिन करें।',
    all: 'सभी',
    selected: 'चुने गए',
    download: 'डाउनलोड',
    noInvoicesYet: 'अभी कोई इनवॉइस नहीं',
    noHistoryWithoutLogin: 'लॉगिन के बिना इतिहास नहीं',
    createFirstInvoice: 'पहली इनवॉइस बनाएं',
    loginToSaveInvoices: 'इनवॉइस सेव करने के लिए लॉगिन करें',
    invoiceHash: 'इनवॉइस #',
    customer: 'ग्राहक',
    status: 'स्थिति',
    dueDate: 'देय तारीख',
    preview: 'प्रीव्यू',
    generatingPreview: 'प्रीव्यू बन रहा है...',
    add: 'जोड़ें',
    addCustomer: 'ग्राहक जोड़ें',
    addItem: 'आइटम जोड़ें',
    template: 'टेम्पलेट',
    csv: 'CSV',
    name: 'नाम',
    selectState: 'राज्य चुनें',
    noCustomersYet: 'अभी कोई ग्राहक नहीं',
    lastTenBrowser: 'इस ब्राउज़र पर अंतिम 10 सेव।',
    customersSource: 'इनवॉइस इतिहास और CSV अपलोड से ग्राहक।',
    itemsSource: 'इनवॉइस इतिहास और CSV अपलोड से आइटम।',
    noItemsYet: 'अभी कोई आइटम नहीं',
    unit: 'यूनिट',
    purchasePrice: 'खरीद मूल्य',
    salePrice: 'बिक्री मूल्य',
    purchase: 'खरीद',
    sale: 'बिक्री',
    confirmingPayment: 'आपका भुगतान कन्फर्म हो रहा है...',
    hangTight: 'कृपया प्रतीक्षा करें। इसमें आमतौर पर 2-5 सेकंड लगते हैं।',
    planActivated: 'प्लान एक्टिवेट हो गया',
    planActiveCopy: 'आपका BillJaldi प्लान एक्टिव है। अब आप इनवॉइस बनाना जारी रख सकते हैं।',
    startCreatingInvoices: 'इनवॉइस बनाना शुरू करें',
    sessionExpired: 'सेशन समाप्त',
    paymentNotConfirmed: 'भुगतान कन्फर्म नहीं हुआ',
    paymentTimeoutCopy: 'अपेक्षित समय में कन्फर्म नहीं हो पाया। कृपया ईमेल देखें या फिर प्रयास करें।',
    paymentRetryCopy: 'कृपया प्लान पेज से checkout दोबारा प्रयास करें।',
    backToPlan: 'प्लान पर वापस',
    shareInvoice: 'इनवॉइस शेयर करें',
    emailWord: 'ईमेल',
    nativeShareUnavailable: 'इस ब्राउज़र में native PDF sharing उपलब्ध नहीं है।',
    pdfDownloadedAttach: 'PDF डाउनलोड हो गया। विंडो खुलने के बाद इसे attach करें।',
    pdfShareFailed: 'शेयर के लिए PDF तैयार नहीं हो सकी।',
    markSentTitle: 'इस इनवॉइस को sent मानें?',
    markSentBody: 'शेयर करने पर यह इनवॉइस sent हो जाएगी और एडिट लॉक हो जाएगा। बाद में paid या cancelled कर सकते हैं, लेकिन सामग्री एडिट नहीं होगी।',
    keepEditing: 'एडिट करते रहें',
    keepAsDraft: 'ड्राफ्ट रखें',
    confirmAndShare: 'कन्फर्म और शेयर',
    clientPlaceholder: 'ग्राहक का नाम या कंपनी',
    streetPlaceholder: 'सड़क, शहर, पिन',
    customTermsPlaceholder: 'रसीद पर देय, अग्रिम भुगतान, milestone शर्तें...',
    notesPlaceholder: 'भुगतान शर्तें, धन्यवाद नोट, या कोई reference.',
    markSentActionBody: 'शेयर करने पर यह इनवॉइस sent हो जाएगी और एडिट लॉक हो जाएगा। History से paid या cancelled कर सकते हैं, लेकिन इनवॉइस सामग्री एडिट नहीं होगी।',
    draft: 'ड्राफ्ट',
    sent: 'भेजा गया',
    paid: 'भुगतान हुआ',
    overdue: 'ओवरड्यू',
    cancelled: 'रद्द',
    selectAllInvoices: 'दिख रहे सभी इनवॉइस चुनें',
    selectInvoice: 'इनवॉइस चुनें',
    downloaded: 'डाउनलोड हुआ',
    invoiceSingular: 'इनवॉइस',
    invoicePlural: 'इनवॉइस',
    deleted: 'डिलीट हुआ',
    statusUpdateFailed: 'स्थिति अपडेट विफल',
    marked: 'मार्क किया',
    couldNotMarkSent: 'इनवॉइस sent मार्क नहीं हो सकी',
    required: 'आवश्यक है',
    saved: 'सेव हुआ',
    savedLocally: 'लोकल सेव हुआ',
    failedToSave: 'सेव नहीं हुआ',
    noCustomersFoundCsv: 'CSV में ग्राहक नहीं मिले',
    noItemsFoundCsv: 'CSV में आइटम नहीं मिले',
    imported: 'इम्पोर्ट हुआ',
    locally: 'लोकल',
    saveInvoicesForever: 'इनवॉइस हमेशा के लिए सेव करें।',
    invoiceHistory: 'इनवॉइस इतिहास',
    searchInvoices: 'इनवॉइस # या ग्राहक खोजें...',
    newestFirst: 'नए पहले',
    oldestFirst: 'पुराने पहले',
    welcomeLine: 'स्वागत है,',
    dashboardHistoryBasis: 'आपके आंकड़े इनवॉइस इतिहास पर आधारित हैं।',
    accountInvoices: 'अकाउंट इनवॉइस',
    accountInvoicesHint: 'इस अकाउंट में मौजूदा मासिक प्लान सीमा के तहत सेव इनवॉइस।',
    invoicesSent: 'भेजी गई इनवॉइस',
    invoicesSentHint: 'Sent या Overdue स्थिति वाली इनवॉइस। Draft शामिल नहीं हैं क्योंकि वे अभी भेजी नहीं गई हैं।',
    accountsReceivable: 'प्राप्त होने वाली राशि',
    accountsReceivableHint: 'ग्राहकों से अभी अपेक्षित राशि। इसमें Sent और Overdue इनवॉइस शामिल हैं, Draft या Cancelled नहीं।',
    accountsReceived: 'प्राप्त राशि',
    accountsReceivedHint: 'Paid मार्क की गई इनवॉइस से आई राशि। सही आंकड़ों के लिए History में स्थिति अपडेट करें।',
    collectionSnapshot: 'कलेक्शन स्नैपशॉट',
    collectionSnapshotCopy: 'इनवॉइस गतिविधि से paid, pending और overdue राशि।',
    collectionSnapshotHint: 'लाल में overdue sent इनवॉइस और past-due drafts शामिल हैं जिन्हें भेजने से पहले समीक्षा चाहिए।',
    details: 'विवरण',
    collectionRate: 'कलेक्शन रेट',
    invoiceValue: 'इनवॉइस वैल्यू',
    drafts: 'ड्राफ्ट',
    pastDue: 'देय तारीख बीती',
    received: 'प्राप्त',
    pending: 'बाकी',
    needsAttention: 'ओवरड्यू / ध्यान चाहिए',
    unlimited: 'अनलिमिटेड',
    monthly: 'मासिक',
    yearly: 'वार्षिक',
    savePercent: '17% बचाएं',
    billedMonthly: 'मासिक बिलिंग',
    billedYearly: 'वार्षिक बिलिंग',
    cancelAnytime: 'कभी भी कैंसल करें',
    starter: 'Starter',
    chooseStarter: 'Starter चुनें',
    openingRazorpay: 'Razorpay खुल रहा है...',
    checkoutFailed: 'Checkout शुरू नहीं हो सका। कृपया फिर प्रयास करें।',
    starterActive: 'Starter पहले से एक्टिव है।',
    activated: 'एक्टिवेट हो गया',
    manualWhatsappShare: 'मैनुअल WhatsApp शेयर',
    accountInvoiceHistory: 'अकाउंट इनवॉइस इतिहास',
    businessProfileAutofill: 'बिजनेस प्रोफाइल और GST ऑटोफिल',
    recurringMandate: 'कैंसल होने तक recurring mandate',
    comingSoon: 'जल्द आ रहा है',
    whatLaunchesNext: 'आगे क्या लॉन्च होगा',
    julyOnward: 'जुलाई से आगे',
    basicPlanSoon: 'Basic प्लान',
    basicPlanSoonCopy: 'अनलिमिटेड इनवॉइस, ledger, GST estimator, profit calculator, outstanding tracker और CA exports.',
    razorpayIntegration: 'Razorpay इंटीग्रेशन',
    razorpayIntegrationCopy: 'पेमेंट इम्पोर्ट करें, इनवॉइस मैच करें और paid, unpaid, partially paid bills ट्रैक करें।',
    metaIntegration: 'Meta इंटीग्रेशन',
    metaIntegrationCopy: 'Seller billing और reconciliation के लिए Meta commerce और ad workflows कनेक्ट करें।',
    shopifyImport: 'Shopify इम्पोर्ट',
    shopifyImportCopy: 'Store sales से orders इम्पोर्ट करें और GST इनवॉइस बनाएं।',
    whatsappAutomation: 'WhatsApp ऑटोमेशन',
    whatsappAutomationCopy: 'WhatsApp पर invoice links, due-date nudges और payment reminders भेजें।',
    codRtoTracker: 'COD/RTO ट्रैकर',
    codRtoTrackerCopy: 'Cash-on-delivery, failed delivery और return-to-origin adjustments ट्रैक करें।',
    returnsRefunds: 'Returns और refunds',
    returnsRefundsCopy: 'Invoice history बिगाड़े बिना return, refund और credit-note adjustments संभालें।',
    sevenYearStorage: '7 साल storage',
    sevenYearStorageCopy: 'Tax records के लिए लंबे समय तक invoice और document retention.',
    accountEmail: 'अकाउंट ईमेल',
    businessEmail: 'बिजनेस ईमेल',
    profileSaved: 'प्रोफाइल सेव हुआ',
    profileSaveFailed: 'प्रोफाइल सेव नहीं हुआ',
    savedOnBrowser: 'इस ब्राउज़र पर सेव हुआ',
    sealSignatureSizeError: 'सील/हस्ताक्षर इमेज 2MB से कम होनी चाहिए',
    sealSignatureAlt: 'सील और हस्ताक्षर',
    sealSignatureHelp: 'संभव हो तो transparent PNG इस्तेमाल करें। यह इमेज invoice preview और downloaded PDF में authorised signature area के पास दिखेगी।',
    wantCloudSync: 'Cloud sync चाहिए?',
    cloudSyncCopy: 'ज्यादा मासिक इनवॉइस और सेव बिजनेस डिटेल्स के लिए Starter अपग्रेड करें।',
    viewPlan: 'प्लान देखें',
    superadmin: 'Superadmin',
    permanent: 'स्थायी',
    sevenDays: '7 दिन',
    fourteenDays: '14 दिन',
    thirtyDays: '30 दिन',
    ninetyDays: '90 दिन',
    superadminPlanOverride: 'Superadmin plan override',
    planOverrideApplied: 'Plan override लागू हुआ',
    planOverrideCleared: 'Plan override हटाया गया',
    saveInvoicesAccount: 'इनवॉइस अपने अकाउंट में सेव करें',
    saveInvoicesAccountCopy: 'Free अकाउंट में महीने के 5 watermarked invoices और history मिलती है। Starter में 100 invoices और saved business details मिलते हैं।',
    invoicesSavedAfterLogin: 'लॉगिन के बाद आपकी इनवॉइस सेव होती हैं',
    invoicesSavedAfterLoginCopy: 'Invoice history के लिए free account बनाएं। Starter में ज्यादा monthly invoices और GST profile autofill मिलता है।',
    upgradeInvoiceLimit: 'इनवॉइस limit अपग्रेड करें',
    upgradeInvoiceLimitCopy: 'Starter में 100 monthly invoices, account history और saved GST business details मिलते हैं।',
    invoicesEverywhere: 'आपकी इनवॉइस, हर जगह',
    invoicesEverywhereCopy: 'Starter में ज्यादा monthly invoices, account history और saved GST business details मिलते हैं।',
    cloudSync: 'Cloud Sync',
    invoiceHistoryFeature: 'Invoice History',
    hundredInvoices: '100 Invoices',
    notNow: 'अभी नहीं',
    seePlan: 'प्लान देखें',
    homeHeroKicker: 'भारतीय sellers के लिए GST invoices',
    homeHeroTitle: 'हर GST invoice के लिए spreadsheet से लड़ना बंद करें।',
    homeHeroCopy: 'BillJaldi साफ GST invoices बनाने, WhatsApp पर शेयर करने और buyer या CA के पूछने पर customer records ready रखने में मदद करता है।',
    startFree: 'Free शुरू करें',
    painTitle: 'Billing की असली परेशानी PDF नहीं है।',
    painCopy: 'Invoice number याद रखना, GST split सही करना, पुराने customer details ढूंढना और payment due होने पर क्या भेजा था दिखाना असली काम है।',
    painOldInvoices: 'पुरानी invoices chats और downloads में खो जाती हैं।',
    painTaxSplit: 'CGST, SGST और IGST checks समय लेते हैं।',
    painFollowup: 'Payment follow-ups WhatsApp पर manually होते हैं।',
    painRecords: 'Month end पर CA-ready records बिखरे रहते हैं।',
    howHelps: 'BillJaldi कैसे मदद करता है',
    helpFastPdf: 'Seconds में GST invoice PDF बनाएं',
    helpAutoTax: 'CGST, SGST और IGST auto-calculate करें',
    helpShare: 'Due date text के साथ WhatsApp या email से share करें',
    helpHistory: 'Login के बाद invoice history save करें',
    helpCustomersItems: 'Saved customers और items reuse करें',
    helpSeal: 'Profile से seal और signature जोड़ें',
    builtFor: 'इनके लिए बनाया गया',
    builtFreelancers: 'Freelancers',
    builtShops: 'छोटी दुकानें',
    builtInstagram: 'Instagram sellers',
    builtServices: 'Service providers',
    builtD2c: 'D2C operators',
    homePlanCopy: 'Free में 5 invoices/month. Starter ₹249/month है. Regular price ₹399.',
    legal: 'Legal',
    privacy: 'Privacy',
    changelog: 'Changelog',
    home: 'Home',
    versionHistory: 'Version history',
    versionHistoryCopy: 'क्या ship हुआ और आगे क्या आ रहा है इसका simple record.',
    currentVersion: 'Current version',
    upcoming: 'Upcoming',
    upcomingCopy: 'v1.1 के बाद planned product tracks.',
    ledgerFeature: 'Ledger',
    ledgerFeatureCopy: 'Customer-wise receivables, paid/unpaid status और account-level views.',
    v10Title: 'v1.0 public launch',
    v10Copy: 'Google sign-in, invoice creation, PDF download, saved history, customers, items, dashboard, Starter payments और admin controls.',
    v11Title: 'v1.1 in progress',
    v11Copy: 'Language support, new landing page, changelog, seal/stamp support और बेहतर mobile navigation.',
    hidePreview: 'छिपाएं',
    invoiceLimitCopy: 'इस महीने की invoice limit पूरी हो गई है। और invoices बनाने के लिए upgrade करें।',
    loggedOut: 'लॉगआउट हो गया',
    signingOut: 'लॉगआउट हो रहा है',
    logoutDoneCopy: 'यह device clean है। Invoice drafts, local history, customers और items clear कर दिए गए।',
    logoutProgressCopy: 'यह device clear हो रहा है और workspace securely बंद हो रहा है।',
    builtMadeIndia: 'भारत में बनाया गया',
    upgrade: 'अपग्रेड',
    free: 'Free',
    expandSidebar: 'साइडबार खोलें',
    collapseSidebar: 'साइडबार बंद करें',
    businessProfile: 'बिजनेस प्रोफाइल',
    previewFailed: 'प्रीव्यू विफल',
    duplicateLimitReached: 'इस महीने की इनवॉइस सीमा पूरी हो गई। डुप्लिकेट करने के लिए अपग्रेड करें।',
    invoiceCopied: 'इनवॉइस कॉपी हुई। जाँचें और डाउनलोड करें।',
    pdfCloudStorageFailed: 'इनवॉइस सेव हुई, लेकिन PDF cloud storage विफल हुआ।',
    watermarkNotice: 'Free और Starter invoices में BillJaldi watermark शामिल है।',
    loginSaveHistoryNotice: 'इनवॉइस इतिहास सेव करने के लिए लॉगिन करें।',
    invoiceDownloaded: 'इनवॉइस डाउनलोड हुई!',
    pdfGenerateFailed: 'PDF बनाने में समस्या हुई। कृपया फिर प्रयास करें।',
    invoiceDisclaimer: 'billjaldi किसी भी बनाई गई इनवॉइस की accuracy, legality या tax compliance के लिए जिम्मेदार नहीं है।',
    logoSizeError: 'लोगो 2MB से कम होना चाहिए',
    loginRightHeadline: 'आपकी इनवॉइस, ग्राहक और आइटम आपके अकाउंट से जुड़े रहते हैं।',
    loginRightCopy: 'साइन इन के बाद BillJaldi अकाउंट इनवॉइस अपने आप सेव करता है। कोई manual cloud migration, अलग password या duplicate signup path नहीं।',
    hundredInvoicesMonth: '100 इनवॉइस/माह',
  },
  ta: {
    createInvoice: 'இன்வாய்ஸ் உருவாக்கு',
    dashboard: 'டாஷ்போர்டு',
    history: 'வரலாறு',
    customers: 'வாடிக்கையாளர்கள்',
    items: 'பொருட்கள்',
    profile: 'சுயவிவரம்',
    adminPanel: 'அட்மின் பேனல்',
    logout: 'வெளியேறு',
    login: 'உள்நுழை',
    guest: 'விருந்தினர்',
    notLoggedIn: 'உள்நுழையவில்லை',
    language: 'மொழி',
    continueWithGoogle: 'Google மூலம் தொடரவும்',
    loginIntro: 'Google மூலம் உள்நுழையவும் அல்லது BillJaldi கணக்கை உருவாக்கவும். கடவுச்சொல் தேவையில்லை. உள்நுழைந்த பிறகு உங்கள் இன்வாய்ஸ் வரலாறு தானாக சேமிக்கப்படும்.',
    loadingGoogle: 'Google உள்நுழைவு ஏற்றப்படுகிறது...',
    googleUnavailable: 'Google உள்நுழைவு இப்போது கிடைக்கவில்லை.',
    verifyingGoogle: 'Google சரிபார்க்கப்படுகிறது, உங்கள் பணிமனை தயார் செய்யப்படுகிறது',
    welcome: 'வரவேற்கிறோம்',
    openingWorkspace: 'வரவேற்கிறோம். உங்கள் பணிமனை திறக்கப்படுகிறது...',
    starterDetail: 'மேம்படுத்திய பிறகு 100 இன்வாய்ஸ்/மாதம்',
    createGstInvoice: 'GST இன்வாய்ஸ் உருவாக்கு',
    livePreview: 'நேரடி முன்னோட்டம்',
    fullScreenPreview: 'முழு திரை முன்னோட்டம்',
    closePreview: 'முன்னோட்டத்தை மூடு',
    limitReached: 'மாதாந்திர இன்வாய்ஸ் வரம்பு முடிந்தது',
    upgradePlan: 'பிளான் மேம்படுத்து',
    viewHistory: 'வரலாறு பார்க்க',
    settings: 'அமைப்புகள்',
    settingsIntro: 'இன்வாய்ஸ் ஆட்டோஃபிலுக்காக GSTIN, மாநிலம், வணிக விவரங்களை சேமிக்கவும்.',
    accountProfile: 'கணக்கு சுயவிவரம்',
    firstName: 'முதல் பெயர்',
    lastName: 'கடைசி பெயர்',
    email: 'மின்னஞ்சல்',
    saveProfile: 'சுயவிவரம் சேமி',
    legalConsent: 'இந்த பயன்பாட்டைப் பயன்படுத்துவதன் மூலம் BillJaldi சட்ட விதிமுறைகள் மற்றும் தரவு பயன்பாட்டிற்கு ஒப்புக்கொள்கிறீர்கள்.',
    legalTerms: 'சட்ட விதிமுறைகள்',
    yourBusiness: 'உங்கள் வணிகம்',
    businessName: 'வணிக பெயர்',
    gstin: 'GSTIN',
    logo: 'லோகோ',
    uploadLogo: 'லோகோ பதிவேற்று',
    remove: 'அகற்று',
    state: 'மாநிலம்',
    phone: 'தொலைபேசி',
    address: 'முகவரி',
    sealSignature: 'முத்திரை மற்றும் கையொப்பம்',
    sealSignatureIntro: 'விருப்பத்தேர்வு. முத்திரை/கையொப்ப படத்தை பதிவேற்றுங்கள்; BillJaldi அதை ஒவ்வொரு இன்வாய்ஸிலும் சேர்க்கும்.',
    uploadSealSignature: 'முத்திரை/கையொப்பம் பதிவேற்று',
    adminOverview: 'அட்மின் மேலோட்டம்',
    adminIntro: 'பயனர்கள், பிளான் கட்டணங்கள், வாடிக்கையாளர் பில்லிங் அளவு.',
    searchUsers: 'பயனர்களை தேடுங்கள்...',
    users: 'பயனர்கள்',
    paidUsers: 'கட்டணம் செலுத்தியவர்கள்',
    planRevenue: 'பிளான் வருவாய்',
    invoiceVolume: 'இன்வாய்ஸ் அளவு',
    invoices: 'இன்வாய்ஸ்',
    user: 'பயனர்',
    plan: 'பிளான்',
    subscription: 'சந்தா',
    planControl: 'பிளான் கட்டுப்பாடு',
    business: 'வணிகம்',
    joined: 'சேர்ந்த நாள்',
    apply: 'பயன்படுத்து',
    clear: 'அழி',
    reasonNote: 'காரணம் / உள் குறிப்பு',
    loading: 'ஏற்றப்படுகிறது...',
    billTo: 'பில் பெறுபவர்',
    pickSaved: 'சேமித்ததை தேர்வு',
    search: 'தேடுங்கள்...',
    noCustomersSaved: 'இன்னும் வாடிக்கையாளர்கள் சேமிக்கப்படவில்லை',
    clearCustomer: 'அழி',
    customerName: 'வாடிக்கையாளர் பெயர்',
    gstinOptional: 'GSTIN (விருப்பம்)',
    placeOfSupply: 'மாநிலம் (வழங்கும் இடம்)',
    emailOptional: 'மின்னஞ்சல் (விருப்பம்)',
    phoneWhatsapp: 'தொலைபேசி / WhatsApp',
    saveCustomerLater: 'வாடிக்கையாளரை சேமி',
    invoiceDetailsPayment: 'இன்வாய்ஸ் விவரங்கள் & கட்டணம்',
    invoiceNumber: 'இன்வாய்ஸ் #',
    date: 'தேதி',
    dueDateOptional: 'கடைசி தேதி (விருப்பம்)',
    paymentTerms: 'கட்டண நிபந்தனைகள்',
    customTerms: 'தனிப்பயன் நிபந்தனைகள்',
    bankName: 'வங்கி பெயர்',
    accountNumber: 'கணக்கு எண்',
    ifsc: 'IFSC',
    upiId: 'UPI ID',
    notesTerms: 'குறிப்புகள் / நிபந்தனைகள்',
    taxSummary: 'வரி சுருக்கம்',
    subtotal: 'துணை மொத்தம்',
    discount: 'தள்ளுபடி',
    taxableAmount: 'வரி விதிக்கத்தக்க தொகை',
    grandTotal: 'மொத்தம்',
    amountInWords: 'தொகை வார்த்தைகளில்',
    downloadPdf: 'PDF பதிவிறக்கு',
    generating: 'உருவாக்கப்படுகிறது...',
    newInvoice: 'புதிய இன்வாய்ஸ்',
    share: 'பகிர்',
    preparing: 'தயார் செய்யப்படுகிறது...',
    itemsServices: 'பொருட்கள் & சேவைகள்',
    delete: 'நீக்கு',
    lineItems: 'வரி உருப்படிகள்',
    description: 'விவரம்',
    qty: 'அளவு',
    rate: 'விலை',
    disc: 'தள்ளு%',
    gst: 'GST',
    amount: 'தொகை',
    duplicate: 'நகல்',
    addRow: 'வரிசை சேர்',
    addFiveRows: '5 வரிசைகள் சேர்',
    accountInvoicesSaved: 'உங்கள் கணக்கு இன்வாய்ஸ்கள் பாதுகாப்பாக சேமிக்கப்படுகின்றன.',
    loginToSaveHistory: 'இன்வாய்ஸ் வரலாற்றை சேமிக்கவும் பார்க்கவும் உள்நுழையவும்.',
    all: 'அனைத்தும்',
    selected: 'தேர்ந்தெடுக்கப்பட்டது',
    download: 'பதிவிறக்கு',
    noInvoicesYet: 'இன்னும் இன்வாய்ஸ் இல்லை',
    noHistoryWithoutLogin: 'உள்நுழைவு இல்லாமல் வரலாறு இல்லை',
    createFirstInvoice: 'முதல் இன்வாய்ஸ் உருவாக்கு',
    loginToSaveInvoices: 'இன்வாய்ஸ்களை சேமிக்க உள்நுழை',
    invoiceHash: 'இன்வாய்ஸ் #',
    customer: 'வாடிக்கையாளர்',
    status: 'நிலை',
    dueDate: 'கடைசி தேதி',
    preview: 'முன்னோட்டம்',
    generatingPreview: 'முன்னோட்டம் உருவாகிறது...',
    add: 'சேர்',
    addCustomer: 'வாடிக்கையாளர் சேர்',
    addItem: 'பொருள் சேர்',
    template: 'டெம்ப்ளேட்',
    csv: 'CSV',
    name: 'பெயர்',
    selectState: 'மாநிலம் தேர்வு',
    noCustomersYet: 'இன்னும் வாடிக்கையாளர்கள் இல்லை',
    lastTenBrowser: 'இந்த உலாவியில் சேமித்த கடைசி 10.',
    customersSource: 'இன்வாய்ஸ் வரலாறு மற்றும் CSV பதிவேற்றங்களில் இருந்து வாடிக்கையாளர்கள்.',
    itemsSource: 'இன்வாய்ஸ் வரலாறு மற்றும் CSV பதிவேற்றங்களில் இருந்து பொருட்கள்.',
    noItemsYet: 'இன்னும் பொருட்கள் இல்லை',
    unit: 'அலகு',
    purchasePrice: 'கொள்முதல் விலை',
    salePrice: 'விற்பனை விலை',
    purchase: 'கொள்முதல்',
    sale: 'விற்பனை',
    confirmingPayment: 'உங்கள் கட்டணம் உறுதிசெய்யப்படுகிறது...',
    hangTight: 'சிறிது நேரம் காத்திருக்கவும். பொதுவாக 2-5 வினாடிகள் ஆகும்.',
    planActivated: 'பிளான் செயல்படுத்தப்பட்டது',
    planActiveCopy: 'உங்கள் BillJaldi பிளான் செயல்பாட்டில் உள்ளது. இன்வாய்ஸ் உருவாக்கலாம்.',
    startCreatingInvoices: 'இன்வாய்ஸ் உருவாக்க தொடங்கு',
    sessionExpired: 'அமர்வு காலாவதியானது',
    paymentNotConfirmed: 'கட்டணம் உறுதியாகவில்லை',
    paymentTimeoutCopy: 'எதிர்பார்த்த நேரத்தில் உறுதி செய்ய முடியவில்லை. மின்னஞ்சலை பார்க்கவும் அல்லது மீண்டும் முயற்சிக்கவும்.',
    paymentRetryCopy: 'பிளான் பக்கத்திலிருந்து checkout மீண்டும் முயற்சிக்கவும்.',
    backToPlan: 'பிளானுக்கு திரும்பு',
    shareInvoice: 'இன்வாய்ஸ் பகிர்',
    emailWord: 'மின்னஞ்சல்',
    nativeShareUnavailable: 'இந்த browser-ல் native PDF sharing கிடைக்கவில்லை.',
    pdfDownloadedAttach: 'PDF பதிவிறக்கப்பட்டது. சாளரம் திறந்த பிறகு attach செய்யவும்.',
    pdfShareFailed: 'பகிர PDF தயார் செய்ய முடியவில்லை.',
    markSentTitle: 'இந்த இன்வாய்ஸை sent என்று குறிக்கவா?',
    markSentBody: 'பகிர்ந்தால் இந்த இன்வாய்ஸ் sent ஆகும் மற்றும் திருத்தம் லாக் ஆகும். பின்னர் paid அல்லது cancelled என மாற்றலாம்; ஆனால் உள்ளடக்கம் திருத்த முடியாது.',
    keepEditing: 'திருத்தத் தொடரவும்',
    keepAsDraft: 'டிராஃப்டாக வைத்திரு',
    confirmAndShare: 'உறுதி செய்து பகிர்',
    clientPlaceholder: 'வாடிக்கையாளர் பெயர் அல்லது நிறுவனம்',
    streetPlaceholder: 'தெரு, நகரம், PIN',
    customTermsPlaceholder: 'பெறும்போது செலுத்தவும், முன்பணம், milestone நிபந்தனைகள்...',
    notesPlaceholder: 'கட்டண நிபந்தனைகள், நன்றி குறிப்பு, அல்லது reference.',
    markSentActionBody: 'பகிர்ந்தால் இந்த இன்வாய்ஸ் sent ஆகும் மற்றும் திருத்தம் லாக் ஆகும். History-யில் paid அல்லது cancelled என மாற்றலாம்; ஆனால் உள்ளடக்கம் திருத்த முடியாது.',
    draft: 'டிராஃப்ட்',
    sent: 'அனுப்பப்பட்டது',
    paid: 'செலுத்தப்பட்டது',
    overdue: 'காலாவதி',
    cancelled: 'ரத்து',
    selectAllInvoices: 'காணும் அனைத்தையும் தேர்வு',
    selectInvoice: 'இன்வாய்ஸ் தேர்வு',
    downloaded: 'பதிவிறக்கம்',
    invoiceSingular: 'இன்வாய்ஸ்',
    invoicePlural: 'இன்வாய்ஸ்கள்',
    deleted: 'நீக்கப்பட்டது',
    statusUpdateFailed: 'நிலை புதுப்பிப்பு தோல்வி',
    marked: 'குறிக்கப்பட்டது',
    couldNotMarkSent: 'இன்வாய்ஸ் sent ஆக குறிக்க முடியவில்லை',
    required: 'தேவை',
    saved: 'சேமிக்கப்பட்டது',
    savedLocally: 'உள்ளூரில் சேமிக்கப்பட்டது',
    failedToSave: 'சேமிக்க முடியவில்லை',
    noCustomersFoundCsv: 'CSV-யில் வாடிக்கையாளர்கள் இல்லை',
    noItemsFoundCsv: 'CSV-யில் பொருட்கள் இல்லை',
    imported: 'இறக்குமதி செய்யப்பட்டது',
    locally: 'உள்ளூரில்',
    saveInvoicesForever: 'இன்வாய்ஸ்களை எப்போதும் சேமிக்கவும்.',
    invoiceHistory: 'இன்வாய்ஸ் வரலாறு',
    searchInvoices: 'இன்வாய்ஸ் # அல்லது வாடிக்கையாளரை தேடுங்கள்...',
    newestFirst: 'புதியவை முதலில்',
    oldestFirst: 'பழையவை முதலில்',
    welcomeLine: 'வரவேற்கிறோம்,',
    dashboardHistoryBasis: 'உங்கள் எண்ணிக்கைகள் இன்வாய்ஸ் வரலாற்றின் அடிப்படையில் உள்ளன.',
    accountInvoices: 'கணக்கு இன்வாய்ஸ்கள்',
    accountInvoicesHint: 'தற்போதைய மாதாந்திர பிளான் வரம்புக்குள் இந்த கணக்கில் சேமிக்கப்பட்ட இன்வாய்ஸ்கள்.',
    invoicesSent: 'அனுப்பிய இன்வாய்ஸ்கள்',
    invoicesSentHint: 'Sent அல்லது Overdue என குறிக்கப்பட்ட இன்வாய்ஸ்கள். Draft சேர்க்கப்படவில்லை.',
    accountsReceivable: 'வர வேண்டிய தொகை',
    accountsReceivableHint: 'வாடிக்கையாளர்களிடமிருந்து இன்னும் வர வேண்டிய பணம். Sent மற்றும் Overdue மட்டும் சேர்க்கப்படும்.',
    accountsReceived: 'பெற்ற தொகை',
    accountsReceivedHint: 'Paid என குறிக்கப்பட்ட இன்வாய்ஸ்களிலிருந்து வந்த பணம். History-யில் நிலையை புதுப்பிக்கவும்.',
    collectionSnapshot: 'வசூல் சுருக்கம்',
    collectionSnapshotCopy: 'இன்வாய்ஸ் செயல்பாட்டிலிருந்து paid, pending மற்றும் overdue தொகை.',
    collectionSnapshotHint: 'சிவப்பு நிறம் overdue sent இன்வாய்ஸ்கள் மற்றும் அனுப்புவதற்கு முன் பார்க்க வேண்டிய past-due drafts.',
    details: 'விவரங்கள்',
    collectionRate: 'வசூல் விகிதம்',
    invoiceValue: 'இன்வாய்ஸ் மதிப்பு',
    drafts: 'டிராஃப்ட்கள்',
    pastDue: 'கடைசி தேதி கடந்தது',
    received: 'பெற்றது',
    pending: 'நிலுவை',
    needsAttention: 'Overdue / கவனம் தேவை',
    unlimited: 'வரம்பில்லை',
    monthly: 'மாதாந்திரம்',
    yearly: 'வருடாந்திரம்',
    savePercent: '17% சேமி',
    billedMonthly: 'மாதந்தோறும் பில்',
    billedYearly: 'வருடந்தோறும் பில்',
    cancelAnytime: 'எப்போது வேண்டுமானாலும் ரத்து செய்யலாம்',
    starter: 'Starter',
    chooseStarter: 'Starter தேர்வு',
    openingRazorpay: 'Razorpay திறக்கிறது...',
    checkoutFailed: 'Checkout தொடங்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.',
    starterActive: 'Starter ஏற்கனவே செயல்பாட்டில் உள்ளது.',
    activated: 'செயல்படுத்தப்பட்டது',
    manualWhatsappShare: 'Manual WhatsApp பகிர்வு',
    accountInvoiceHistory: 'கணக்கு இன்வாய்ஸ் வரலாறு',
    businessProfileAutofill: 'வணிக சுயவிவரம் மற்றும் GST autofill',
    recurringMandate: 'ரத்து செய்யும் வரை recurring mandate',
    comingSoon: 'விரைவில்',
    whatLaunchesNext: 'அடுத்ததாக வரும் அம்சங்கள்',
    julyOnward: 'ஜூலை முதல்',
    basicPlanSoon: 'Basic பிளான்',
    basicPlanSoonCopy: 'வரம்பற்ற இன்வாய்ஸ்கள், ledger, GST estimator, profit calculator, outstanding tracker மற்றும் CA exports.',
    razorpayIntegration: 'Razorpay ஒருங்கிணைப்பு',
    razorpayIntegrationCopy: 'கட்டணங்களை இறக்குமதி செய்து, இன்வாய்ஸ்களுடன் பொருத்தி, paid/unpaid நிலையை கண்காணிக்கவும்.',
    metaIntegration: 'Meta ஒருங்கிணைப்பு',
    metaIntegrationCopy: 'Seller billing மற்றும் reconciliation க்காக Meta commerce/ad workflows இணைக்கவும்.',
    shopifyImport: 'Shopify இறக்குமதி',
    shopifyImportCopy: 'Store sales-இல் இருந்து orders இறக்குமதி செய்து GST இன்வாய்ஸ் உருவாக்கவும்.',
    whatsappAutomation: 'WhatsApp automation',
    whatsappAutomationCopy: 'WhatsApp வழியாக invoice links, due-date nudges, payment reminders அனுப்பவும்.',
    codRtoTracker: 'COD/RTO tracker',
    codRtoTrackerCopy: 'Cash-on-delivery, failed delivery, return-to-origin adjustments கண்காணிக்கவும்.',
    returnsRefunds: 'Returns மற்றும் refunds',
    returnsRefundsCopy: 'Invoice history பாதிக்காமல் return, refund, credit-note adjustments கையாளவும்.',
    sevenYearStorage: '7 ஆண்டு storage',
    sevenYearStorageCopy: 'Tax records க்கான நீண்டகால invoice மற்றும் document retention.',
    accountEmail: 'கணக்கு மின்னஞ்சல்',
    businessEmail: 'வணிக மின்னஞ்சல்',
    profileSaved: 'சுயவிவரம் சேமிக்கப்பட்டது',
    profileSaveFailed: 'சுயவிவரம் சேமிக்க முடியவில்லை',
    savedOnBrowser: 'இந்த browser-ல் சேமிக்கப்பட்டது',
    sealSignatureSizeError: 'முத்திரை/கையொப்ப படம் 2MB க்குள் இருக்க வேண்டும்',
    sealSignatureAlt: 'முத்திரை மற்றும் கையொப்பம்',
    sealSignatureHelp: 'சாத்தியமெனில் transparent PNG பயன்படுத்தவும். படம் invoice preview மற்றும் downloaded PDF-ல் authorised signature பகுதியில் தோன்றும்.',
    wantCloudSync: 'Cloud sync வேண்டுமா?',
    cloudSyncCopy: 'மேலும் மாதாந்திர இன்வாய்ஸ்கள் மற்றும் சேமித்த வணிக விவரங்களுக்கு Starter-க்கு மேம்படுத்தவும்.',
    viewPlan: 'பிளான் பார்க்க',
    superadmin: 'Superadmin',
    permanent: 'நிரந்தரம்',
    sevenDays: '7 நாட்கள்',
    fourteenDays: '14 நாட்கள்',
    thirtyDays: '30 நாட்கள்',
    ninetyDays: '90 நாட்கள்',
    superadminPlanOverride: 'Superadmin plan override',
    planOverrideApplied: 'Plan override பயன்படுத்தப்பட்டது',
    planOverrideCleared: 'Plan override அழிக்கப்பட்டது',
    saveInvoicesAccount: 'இன்வாய்ஸ்களை உங்கள் கணக்கில் சேமிக்கவும்',
    saveInvoicesAccountCopy: 'Free கணக்கில் மாதம் 5 watermarked invoices மற்றும் history உண்டு. Starter 100 invoices மற்றும் saved business details தரும்.',
    invoicesSavedAfterLogin: 'உள்நுழைந்த பிறகு உங்கள் இன்வாய்ஸ்கள் சேமிக்கப்படும்',
    invoicesSavedAfterLoginCopy: 'Invoice history க்காக free account உருவாக்கவும். Starter மேலும் monthly invoices மற்றும் GST profile autofill சேர்க்கும்.',
    upgradeInvoiceLimit: 'இன்வாய்ஸ் வரம்பை மேம்படுத்து',
    upgradeInvoiceLimitCopy: 'Starter 100 monthly invoices, account history, saved GST business details தரும்.',
    invoicesEverywhere: 'உங்கள் இன்வாய்ஸ்கள், எங்கும்',
    invoicesEverywhereCopy: 'Starter மேலும் monthly invoices, account history, saved GST business details தரும்.',
    cloudSync: 'Cloud Sync',
    invoiceHistoryFeature: 'Invoice History',
    hundredInvoices: '100 Invoices',
    notNow: 'இப்போது வேண்டாம்',
    seePlan: 'பிளான் பார்க்க',
    homeHeroKicker: 'இந்திய sellers க்கான GST invoices',
    homeHeroTitle: 'ஒவ்வொரு GST invoice க்கும் spreadsheet சிரமம் வேண்டாம்.',
    homeHeroCopy: 'BillJaldi சுத்தமான GST invoices உருவாக்க, WhatsApp-ல் பகிர, buyer அல்லது CA கேட்கும் போது customer records தயாராக வைத்திருக்க உதவும்.',
    startFree: 'Free தொடங்கு',
    painTitle: 'Billing-ன் சிரமம் PDF மட்டும் அல்ல.',
    painCopy: 'Invoice numbers நினைவில் வைப்பது, GST split சரிபார்ப்பது, பழைய customer details கண்டுபிடிப்பது, payment due ஆனபோது என்ன அனுப்பினோம் என்பதைக் காட்டுவது தான் வேலை.',
    painOldInvoices: 'பழைய invoices chats மற்றும் downloads-ல் காணாமல் போகும்.',
    painTaxSplit: 'CGST, SGST, IGST checks நேரம் எடுக்கும்.',
    painFollowup: 'Payment follow-ups WhatsApp-ல் manual ஆக நடக்கும்.',
    painRecords: 'Month end-ல் CA-ready records சிதறி இருக்கும்.',
    howHelps: 'BillJaldi எப்படி உதவுகிறது',
    helpFastPdf: 'Seconds-ல் GST invoice PDF உருவாக்கு',
    helpAutoTax: 'CGST, SGST, IGST auto-calculate',
    helpShare: 'Due date text உடன் WhatsApp அல்லது email-ல் share',
    helpHistory: 'Login பிறகு invoice history save',
    helpCustomersItems: 'Saved customers மற்றும் items reuse',
    helpSeal: 'Profile-ல் இருந்து seal/signature சேர்க்க',
    builtFor: 'யாருக்காக',
    builtFreelancers: 'Freelancers',
    builtShops: 'சிறு கடைகள்',
    builtInstagram: 'Instagram sellers',
    builtServices: 'Service providers',
    builtD2c: 'D2C operators',
    homePlanCopy: 'Free: 5 invoices/month. Starter ₹249/month. Regular price ₹399.',
    legal: 'Legal',
    privacy: 'Privacy',
    changelog: 'Changelog',
    home: 'Home',
    versionHistory: 'Version history',
    versionHistoryCopy: 'எது shipped, அடுத்து என்ன வருகிறது என்பதற்கான simple record.',
    currentVersion: 'Current version',
    upcoming: 'Upcoming',
    upcomingCopy: 'v1.1 பிறகு planned product tracks.',
    ledgerFeature: 'Ledger',
    ledgerFeatureCopy: 'Customer-wise receivables, paid/unpaid status, account-level views.',
    v10Title: 'v1.0 public launch',
    v10Copy: 'Google sign-in, invoice creation, PDF download, saved history, customers, items, dashboard, Starter payments, admin controls.',
    v11Title: 'v1.1 in progress',
    v11Copy: 'Language support, new landing page, changelog, seal/stamp support, better mobile navigation.',
    hidePreview: 'மறை',
    invoiceLimitCopy: 'இந்த மாத invoice limit முடிந்தது. மேலும் invoices உருவாக்க upgrade செய்யவும்.',
    loggedOut: 'வெளியேறிவிட்டீர்கள்',
    signingOut: 'வெளியேறுகிறது',
    logoutDoneCopy: 'இந்த device clean. Invoice drafts, local history, customers, items clear செய்யப்பட்டது.',
    logoutProgressCopy: 'இந்த device clear ஆகிறது, workspace securely மூடப்படுகிறது.',
    builtMadeIndia: 'இந்தியாவில் உருவாக்கப்பட்டது',
    upgrade: 'மேம்படுத்து',
    free: 'Free',
    expandSidebar: 'பக்கப்பட்டி திற',
    collapseSidebar: 'பக்கப்பட்டி சுருக்கு',
    businessProfile: 'வணிக சுயவிவரம்',
    previewFailed: 'முன்னோட்டம் தோல்வி',
    duplicateLimitReached: 'இந்த மாத இன்வாய்ஸ் வரம்பு முடிந்தது. நகலெடுக்க மேம்படுத்தவும்.',
    invoiceCopied: 'இன்வாய்ஸ் நகலெடுக்கப்பட்டது. சரிபார்த்து பதிவிறக்கவும்.',
    pdfCloudStorageFailed: 'இன்வாய்ஸ் சேமிக்கப்பட்டது, ஆனால் PDF cloud storage தோல்வி.',
    watermarkNotice: 'Free மற்றும் Starter invoices-ல் BillJaldi watermark இருக்கும்.',
    loginSaveHistoryNotice: 'இன்வாய்ஸ் வரலாறு சேமிக்க உள்நுழையவும்.',
    invoiceDownloaded: 'இன்வாய்ஸ் பதிவிறங்கியது!',
    pdfGenerateFailed: 'PDF உருவாக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.',
    invoiceDisclaimer: 'உருவாக்கப்படும் எந்த இன்வாய்ஸின் accuracy, legality அல்லது tax compliance க்கும் billjaldi பொறுப்பல்ல.',
    logoSizeError: 'லோகோ 2MB க்குள் இருக்க வேண்டும்',
    loginRightHeadline: 'உங்கள் இன்வாய்ஸ், வாடிக்கையாளர்கள், பொருட்கள் உங்கள் கணக்குடன் இணைந்தே இருக்கும்.',
    loginRightCopy: 'உள்நுழைந்த பிறகு BillJaldi கணக்கு இன்வாய்ஸ்களை தானாக சேமிக்கும். Manual cloud migration, தனி password, duplicate signup path இல்லை.',
    hundredInvoicesMonth: '100 இன்வாய்ஸ்/மாதம்',
  },
  kn: {
    createInvoice: 'ಇನ್ವಾಯ್ಸ್ ರಚಿಸಿ',
    dashboard: 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್',
    history: 'ಇತಿಹಾಸ',
    customers: 'ಗ್ರಾಹಕರು',
    items: 'ಐಟಂಗಳು',
    profile: 'ಪ್ರೊಫೈಲ್',
    adminPanel: 'ಅಡ್ಮಿನ್ ಪ್ಯಾನಲ್',
    logout: 'ಲಾಗ್ ಔಟ್',
    login: 'ಲಾಗಿನ್',
    guest: 'ಅತಿಥಿ',
    notLoggedIn: 'ಲಾಗಿನ್ ಆಗಿಲ್ಲ',
    language: 'ಭಾಷೆ',
    continueWithGoogle: 'Google ಮೂಲಕ ಮುಂದುವರಿಸಿ',
    loginIntro: 'Google ಮೂಲಕ ಸೈನ್ ಇನ್ ಮಾಡಿ ಅಥವಾ BillJaldi ಖಾತೆ ರಚಿಸಿ. ಪಾಸ್‌ವರ್ಡ್ ಬೇಡ. ಲಾಗಿನ್ ಆದ ನಂತರ ನಿಮ್ಮ ಇನ್ವಾಯ್ಸ್ ಇತಿಹಾಸ ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಉಳಿಯುತ್ತದೆ.',
    loadingGoogle: 'Google ಸೈನ್-ಇನ್ ಲೋಡ್ ಆಗುತ್ತಿದೆ...',
    googleUnavailable: 'Google ಸೈನ್-ಇನ್ ಈಗ ಲಭ್ಯವಿಲ್ಲ.',
    verifyingGoogle: 'Google ಪರಿಶೀಲನೆ ಮತ್ತು ನಿಮ್ಮ ವರ್ಕ್‌ಸ್ಪೇಸ್ ಸಿದ್ಧಪಡಿಸಲಾಗುತ್ತಿದೆ',
    welcome: 'ಸ್ವಾಗತ',
    openingWorkspace: 'ಸ್ವಾಗತ. ನಿಮ್ಮ ವರ್ಕ್‌ಸ್ಪೇಸ್ ತೆರೆಯಲಾಗುತ್ತಿದೆ...',
    starterDetail: 'ಅಪ್‌ಗ್ರೇಡ್ ನಂತರ 100 ಇನ್ವಾಯ್ಸ್/ತಿಂಗಳು',
    createGstInvoice: 'GST ಇನ್ವಾಯ್ಸ್ ರಚಿಸಿ',
    livePreview: 'ಲೈವ್ ಪೂರ್ವವೀಕ್ಷಣೆ',
    fullScreenPreview: 'ಪೂರ್ಣ ಪರದೆ ಪೂರ್ವವೀಕ್ಷಣೆ',
    closePreview: 'ಪೂರ್ವವೀಕ್ಷಣೆ ಮುಚ್ಚಿ',
    limitReached: 'ಮಾಸಿಕ ಇನ್ವಾಯ್ಸ್ ಮಿತಿ ಮುಗಿದಿದೆ',
    upgradePlan: 'ಪ್ಲಾನ್ ಅಪ್‌ಗ್ರೇಡ್ ಮಾಡಿ',
    viewHistory: 'ಇತಿಹಾಸ ನೋಡಿ',
    settings: 'ಸೆಟ್ಟಿಂಗ್‌ಗಳು',
    settingsIntro: 'ಇನ್ವಾಯ್ಸ್ ಆಟೋಫಿಲ್‌ಗಾಗಿ GSTIN, ರಾಜ್ಯ ಮತ್ತು ವ್ಯವಹಾರ ವಿವರಗಳನ್ನು ಉಳಿಸಿ.',
    accountProfile: 'ಖಾತೆ ಪ್ರೊಫೈಲ್',
    firstName: 'ಮೊದಲ ಹೆಸರು',
    lastName: 'ಕೊನೆಯ ಹೆಸರು',
    email: 'ಇಮೇಲ್',
    saveProfile: 'ಪ್ರೊಫೈಲ್ ಉಳಿಸಿ',
    legalConsent: 'ಈ ಅಪ್ಲಿಕೇಶನ್ ಬಳಸುವುದರಿಂದ BillJaldi ಕಾನೂನು ನಿಯಮಗಳು ಮತ್ತು ಡೇಟಾ ಬಳಕೆಗೆ ನೀವು ಒಪ್ಪುತ್ತೀರಿ.',
    legalTerms: 'ಕಾನೂನು ನಿಯಮಗಳು',
    yourBusiness: 'ನಿಮ್ಮ ವ್ಯವಹಾರ',
    businessName: 'ವ್ಯವಹಾರ ಹೆಸರು',
    gstin: 'GSTIN',
    logo: 'ಲೋಗೋ',
    uploadLogo: 'ಲೋಗೋ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ',
    remove: 'ತೆಗೆದುಹಾಕಿ',
    state: 'ರಾಜ್ಯ',
    phone: 'ಫೋನ್',
    address: 'ವಿಳಾಸ',
    sealSignature: 'ಸೀಲ್ ಮತ್ತು ಸಹಿ',
    sealSignatureIntro: 'ಐಚ್ಛಿಕ. ಸೀಲ್/ಸಹಿ ಚಿತ್ರವನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ; BillJaldi ಅದನ್ನು ಪ್ರತಿಯೊಂದು ಇನ್ವಾಯ್ಸ್‌ಗೆ ಸೇರಿಸುತ್ತದೆ.',
    uploadSealSignature: 'ಸೀಲ್/ಸಹಿ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ',
    adminOverview: 'ಅಡ್ಮಿನ್ ಅವಲೋಕನ',
    adminIntro: 'ಬಳಕೆದಾರರು, ಪ್ಲಾನ್ ಪಾವತಿಗಳು ಮತ್ತು ಗ್ರಾಹಕ ಬಿಲ್ಲಿಂಗ್ ಪ್ರಮಾಣ.',
    searchUsers: 'ಬಳಕೆದಾರರನ್ನು ಹುಡುಕಿ...',
    users: 'ಬಳಕೆದಾರರು',
    paidUsers: 'ಪಾವತಿಸಿದ ಬಳಕೆದಾರರು',
    planRevenue: 'ಪ್ಲಾನ್ ಆದಾಯ',
    invoiceVolume: 'ಇನ್ವಾಯ್ಸ್ ಪ್ರಮಾಣ',
    invoices: 'ಇನ್ವಾಯ್ಸ್‌ಗಳು',
    user: 'ಬಳಕೆದಾರ',
    plan: 'ಪ್ಲಾನ್',
    subscription: 'ಚಂದಾದಾರಿಕೆ',
    planControl: 'ಪ್ಲಾನ್ ನಿಯಂತ್ರಣ',
    business: 'ವ್ಯವಹಾರ',
    joined: 'ಸೇರಿದ ದಿನ',
    apply: 'ಅನ್ವಯಿಸಿ',
    clear: 'ತೆರವುಗೊಳಿಸಿ',
    reasonNote: 'ಕಾರಣ / ಆಂತರಿಕ ಟಿಪ್ಪಣಿ',
    loading: 'ಲೋಡ್ ಆಗುತ್ತಿದೆ...',
    billTo: 'ಬಿಲ್ ಸ್ವೀಕರಿಸುವವರು',
    pickSaved: 'ಉಳಿಸಿದದ್ದನ್ನು ಆರಿಸಿ',
    search: 'ಹುಡುಕಿ...',
    noCustomersSaved: 'ಇನ್ನೂ ಗ್ರಾಹಕರು ಉಳಿಸಲ್ಪಟ್ಟಿಲ್ಲ',
    clearCustomer: 'ತೆರವು',
    customerName: 'ಗ್ರಾಹಕ ಹೆಸರು',
    gstinOptional: 'GSTIN (ಐಚ್ಛಿಕ)',
    placeOfSupply: 'ರಾಜ್ಯ (ಸರಬರಾಜು ಸ್ಥಳ)',
    emailOptional: 'ಇಮೇಲ್ (ಐಚ್ಛಿಕ)',
    phoneWhatsapp: 'ಫೋನ್ / WhatsApp',
    saveCustomerLater: 'ಗ್ರಾಹಕರನ್ನು ಉಳಿಸಿ',
    invoiceDetailsPayment: 'ಇನ್ವಾಯ್ಸ್ ವಿವರಗಳು ಮತ್ತು ಪಾವತಿ',
    invoiceNumber: 'ಇನ್ವಾಯ್ಸ್ #',
    date: 'ದಿನಾಂಕ',
    dueDateOptional: 'ಪಾವತಿ ದಿನಾಂಕ (ಐಚ್ಛಿಕ)',
    paymentTerms: 'ಪಾವತಿ ನಿಯಮಗಳು',
    customTerms: 'ಕಸ್ಟಮ್ ನಿಯಮಗಳು',
    bankName: 'ಬ್ಯಾಂಕ್ ಹೆಸರು',
    accountNumber: 'ಖಾತೆ ಸಂಖ್ಯೆ',
    ifsc: 'IFSC',
    upiId: 'UPI ID',
    notesTerms: 'ಟಿಪ್ಪಣಿಗಳು / ನಿಯಮಗಳು',
    taxSummary: 'ತೆರಿಗೆ ಸಾರಾಂಶ',
    subtotal: 'ಉಪಮೊತ್ತ',
    discount: 'ರಿಯಾಯಿತಿ',
    taxableAmount: 'ತೆರಿಗೆ ವಿಧಿಸಬಹುದಾದ ಮೊತ್ತ',
    grandTotal: 'ಒಟ್ಟು ಮೊತ್ತ',
    amountInWords: 'ಪದಗಳಲ್ಲಿ ಮೊತ್ತ',
    downloadPdf: 'PDF ಡೌನ್‌ಲೋಡ್',
    generating: 'ರಚಿಸಲಾಗುತ್ತಿದೆ...',
    newInvoice: 'ಹೊಸ ಇನ್ವಾಯ್ಸ್',
    share: 'ಹಂಚಿಕೊಳ್ಳಿ',
    preparing: 'ಸಿದ್ಧಪಡಿಸಲಾಗುತ್ತಿದೆ...',
    itemsServices: 'ಐಟಂಗಳು ಮತ್ತು ಸೇವೆಗಳು',
    delete: 'ಅಳಿಸಿ',
    lineItems: 'ಲೈನ್ ಐಟಂಗಳು',
    description: 'ವಿವರಣೆ',
    qty: 'ಪ್ರಮಾಣ',
    rate: 'ದರ',
    disc: 'ರಿಯಾಯಿತಿ%',
    gst: 'GST',
    amount: 'ಮೊತ್ತ',
    duplicate: 'ನಕಲು',
    addRow: 'ಸಾಲು ಸೇರಿಸಿ',
    addFiveRows: '5 ಸಾಲು ಸೇರಿಸಿ',
    accountInvoicesSaved: 'ನಿಮ್ಮ ಖಾತೆ ಇನ್ವಾಯ್ಸ್‌ಗಳು ಸುರಕ್ಷಿತವಾಗಿ ಉಳಿಸಲ್ಪಟ್ಟಿವೆ.',
    loginToSaveHistory: 'ಇನ್ವಾಯ್ಸ್ ಇತಿಹಾಸ ಉಳಿಸಲು ಮತ್ತು ನೋಡಲು ಲಾಗಿನ್ ಮಾಡಿ.',
    all: 'ಎಲ್ಲಾ',
    selected: 'ಆಯ್ಕೆ',
    download: 'ಡೌನ್‌ಲೋಡ್',
    noInvoicesYet: 'ಇನ್ನೂ ಇನ್ವಾಯ್ಸ್ ಇಲ್ಲ',
    noHistoryWithoutLogin: 'ಲಾಗಿನ್ ಇಲ್ಲದೆ ಇತಿಹಾಸ ಇಲ್ಲ',
    createFirstInvoice: 'ಮೊದಲ ಇನ್ವಾಯ್ಸ್ ರಚಿಸಿ',
    loginToSaveInvoices: 'ಇನ್ವಾಯ್ಸ್ ಉಳಿಸಲು ಲಾಗಿನ್ ಮಾಡಿ',
    invoiceHash: 'ಇನ್ವಾಯ್ಸ್ #',
    customer: 'ಗ್ರಾಹಕ',
    status: 'ಸ್ಥಿತಿ',
    dueDate: 'ಪಾವತಿ ದಿನಾಂಕ',
    preview: 'ಪೂರ್ವವೀಕ್ಷಣೆ',
    generatingPreview: 'ಪೂರ್ವವೀಕ್ಷಣೆ ರಚಿಸಲಾಗುತ್ತಿದೆ...',
    add: 'ಸೇರಿಸಿ',
    addCustomer: 'ಗ್ರಾಹಕ ಸೇರಿಸಿ',
    addItem: 'ಐಟಂ ಸೇರಿಸಿ',
    template: 'ಟೆಂಪ್ಲೇಟ್',
    csv: 'CSV',
    name: 'ಹೆಸರು',
    selectState: 'ರಾಜ್ಯ ಆಯ್ಕೆ',
    noCustomersYet: 'ಇನ್ನೂ ಗ್ರಾಹಕರು ಇಲ್ಲ',
    lastTenBrowser: 'ಈ ಬ್ರೌಸರ್‌ನಲ್ಲಿ ಉಳಿಸಿದ ಕೊನೆಯ 10.',
    customersSource: 'ಇನ್ವಾಯ್ಸ್ ಇತಿಹಾಸ ಮತ್ತು CSV ಅಪ್‌ಲೋಡ್‌ಗಳಿಂದ ಗ್ರಾಹಕರು.',
    itemsSource: 'ಇನ್ವಾಯ್ಸ್ ಇತಿಹಾಸ ಮತ್ತು CSV ಅಪ್‌ಲೋಡ್‌ಗಳಿಂದ ಐಟಂಗಳು.',
    noItemsYet: 'ಇನ್ನೂ ಐಟಂಗಳು ಇಲ್ಲ',
    unit: 'ಯುನಿಟ್',
    purchasePrice: 'ಖರೀದಿ ಬೆಲೆ',
    salePrice: 'ಮಾರಾಟ ಬೆಲೆ',
    purchase: 'ಖರೀದಿ',
    sale: 'ಮಾರಾಟ',
    confirmingPayment: 'ನಿಮ್ಮ ಪಾವತಿ ದೃಢೀಕರಿಸಲಾಗುತ್ತಿದೆ...',
    hangTight: 'ಸ್ವಲ್ಪ ಕಾಯಿರಿ. ಸಾಮಾನ್ಯವಾಗಿ 2-5 ಸೆಕೆಂಡ್ ಬೇಕಾಗುತ್ತದೆ.',
    planActivated: 'ಪ್ಲಾನ್ ಸಕ್ರಿಯವಾಗಿದೆ',
    planActiveCopy: 'ನಿಮ್ಮ BillJaldi ಪ್ಲಾನ್ ಸಕ್ರಿಯವಾಗಿದೆ. ಈಗ ಇನ್ವಾಯ್ಸ್ ರಚಿಸಬಹುದು.',
    startCreatingInvoices: 'ಇನ್ವಾಯ್ಸ್ ರಚಿಸಲು ಪ್ರಾರಂಭಿಸಿ',
    sessionExpired: 'ಸೆಷನ್ ಅವಧಿ ಮುಗಿದಿದೆ',
    paymentNotConfirmed: 'ಪಾವತಿ ದೃಢೀಕರಿಸಲಿಲ್ಲ',
    paymentTimeoutCopy: 'ನಿರೀಕ್ಷಿತ ಸಮಯದಲ್ಲಿ ದೃಢೀಕರಿಸಲು ಆಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಇಮೇಲ್ ಪರಿಶೀಲಿಸಿ ಅಥವಾ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    paymentRetryCopy: 'ದಯವಿಟ್ಟು ಪ್ಲಾನ್ ಪುಟದಿಂದ checkout ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    backToPlan: 'ಪ್ಲಾನ್‌ಗೆ ಹಿಂತಿರುಗಿ',
    shareInvoice: 'ಇನ್ವಾಯ್ಸ್ ಹಂಚಿಕೊಳ್ಳಿ',
    emailWord: 'ಇಮೇಲ್',
    nativeShareUnavailable: 'ಈ browser ನಲ್ಲಿ native PDF sharing ಲಭ್ಯವಿಲ್ಲ.',
    pdfDownloadedAttach: 'PDF ಡೌನ್‌ಲೋಡ್ ಆಗಿದೆ. ವಿಂಡೋ ತೆರೆದ ನಂತರ attach ಮಾಡಿ.',
    pdfShareFailed: 'ಹಂಚಲು PDF ಸಿದ್ಧಪಡಿಸಲಾಗಲಿಲ್ಲ.',
    markSentTitle: 'ಈ ಇನ್ವಾಯ್ಸ್ ಅನ್ನು sent ಎಂದು ಗುರುತಿಸಬೇಕೇ?',
    markSentBody: 'ಹಂಚಿದರೆ ಈ ಇನ್ವಾಯ್ಸ್ sent ಆಗಿ, ತಿದ್ದುಪಡಿ ಲಾಕ್ ಆಗುತ್ತದೆ. ನಂತರ paid ಅಥವಾ cancelled ಮಾಡಬಹುದು, ಆದರೆ ವಿಷಯವನ್ನು ತಿದ್ದುಪಡಿ ಮಾಡಲು ಆಗುವುದಿಲ್ಲ.',
    keepEditing: 'ತಿದ್ದುಪಡಿ ಮುಂದುವರಿಸಿ',
    keepAsDraft: 'ಡ್ರಾಫ್ಟ್ ಆಗಿ ಇಡಿ',
    confirmAndShare: 'ದೃಢೀಕರಿಸಿ ಹಂಚಿಕೊಳ್ಳಿ',
    clientPlaceholder: 'ಗ್ರಾಹಕರ ಪೂರ್ಣ ಹೆಸರು ಅಥವಾ ಕಂಪನಿ',
    streetPlaceholder: 'ರಸ್ತೆ, ನಗರ, PIN',
    customTermsPlaceholder: 'ಸ್ವೀಕರಿಸಿದಾಗ ಪಾವತಿ, ಮುಂಗಡ, milestone ನಿಯಮಗಳು...',
    notesPlaceholder: 'ಪಾವತಿ ನಿಯಮಗಳು, ಧನ್ಯವಾದ ಟಿಪ್ಪಣಿ, ಅಥವಾ reference.',
    markSentActionBody: 'ಹಂಚಿದರೆ ಈ ಇನ್ವಾಯ್ಸ್ sent ಆಗಿ, ತಿದ್ದುಪಡಿ ಲಾಕ್ ಆಗುತ್ತದೆ. History-ಯಲ್ಲಿ paid ಅಥವಾ cancelled ಮಾಡಬಹುದು, ಆದರೆ ವಿಷಯ ತಿದ್ದುಪಡಿ ಆಗುವುದಿಲ್ಲ.',
    draft: 'ಡ್ರಾಫ್ಟ್',
    sent: 'ಕಳುಹಿಸಲಾಗಿದೆ',
    paid: 'ಪಾವತಿಸಲಾಗಿದೆ',
    overdue: 'ಅವಧಿ ಮೀರಿದೆ',
    cancelled: 'ರದ್ದು',
    selectAllInvoices: 'ಕಾಣುವ ಎಲ್ಲಾ ಇನ್ವಾಯ್ಸ್ ಆಯ್ಕೆ',
    selectInvoice: 'ಇನ್ವಾಯ್ಸ್ ಆಯ್ಕೆ',
    downloaded: 'ಡೌನ್‌ಲೋಡ್ ಆಗಿದೆ',
    invoiceSingular: 'ಇನ್ವಾಯ್ಸ್',
    invoicePlural: 'ಇನ್ವಾಯ್ಸ್‌ಗಳು',
    deleted: 'ಅಳಿಸಲಾಗಿದೆ',
    statusUpdateFailed: 'ಸ್ಥಿತಿ ಅಪ್‌ಡೇಟ್ ವಿಫಲ',
    marked: 'ಗುರುತಿಸಲಾಗಿದೆ',
    couldNotMarkSent: 'ಇನ್ವಾಯ್ಸ್ sent ಗುರುತಿಸಲಾಗಲಿಲ್ಲ',
    required: 'ಅಗತ್ಯ',
    saved: 'ಉಳಿಸಲಾಗಿದೆ',
    savedLocally: 'ಸ್ಥಳೀಯವಾಗಿ ಉಳಿಸಲಾಗಿದೆ',
    failedToSave: 'ಉಳಿಸಲು ವಿಫಲ',
    noCustomersFoundCsv: 'CSV ನಲ್ಲಿ ಗ್ರಾಹಕರು ಕಂಡುಬಂದಿಲ್ಲ',
    noItemsFoundCsv: 'CSV ನಲ್ಲಿ ಐಟಂಗಳು ಕಂಡುಬಂದಿಲ್ಲ',
    imported: 'ಆಮದು ಮಾಡಿದೆ',
    locally: 'ಸ್ಥಳೀಯವಾಗಿ',
    saveInvoicesForever: 'ಇನ್ವಾಯ್ಸ್‌ಗಳನ್ನು ಸದಾ ಉಳಿಸಿ.',
    invoiceHistory: 'ಇನ್ವಾಯ್ಸ್ ಇತಿಹಾಸ',
    searchInvoices: 'ಇನ್ವಾಯ್ಸ್ # ಅಥವಾ ಗ್ರಾಹಕರನ್ನು ಹುಡುಕಿ...',
    newestFirst: 'ಹೊಸವು ಮೊದಲು',
    oldestFirst: 'ಹಳೆಯವು ಮೊದಲು',
    welcomeLine: 'ಸ್ವಾಗತ,',
    dashboardHistoryBasis: 'ನಿಮ್ಮ ಸಂಖ್ಯೆಗಳು ಇನ್ವಾಯ್ಸ್ ಇತಿಹಾಸದ ಆಧಾರದಲ್ಲಿವೆ.',
    accountInvoices: 'ಖಾತೆ ಇನ್ವಾಯ್ಸ್‌ಗಳು',
    accountInvoicesHint: 'ಪ್ರಸ್ತುತ ಮಾಸಿಕ ಪ್ಲಾನ್ ಮಿತಿಯೊಳಗೆ ಈ ಖಾತೆಯಲ್ಲಿ ಉಳಿಸಿದ ಇನ್ವಾಯ್ಸ್‌ಗಳು.',
    invoicesSent: 'ಕಳುಹಿಸಿದ ಇನ್ವಾಯ್ಸ್‌ಗಳು',
    invoicesSentHint: 'Sent ಅಥವಾ Overdue ಎಂದು ಗುರುತಿಸಿದ ಇನ್ವಾಯ್ಸ್‌ಗಳು. Draft ಸೇರಿಲ್ಲ.',
    accountsReceivable: 'ಬಾಕಿ ಬರಬೇಕಾದ ಮೊತ್ತ',
    accountsReceivableHint: 'ಗ್ರಾಹಕರಿಂದ ಇನ್ನೂ ಬರಬೇಕಾದ ಹಣ. Sent ಮತ್ತು Overdue ಮಾತ್ರ ಸೇರಿವೆ.',
    accountsReceived: 'ಸ್ವೀಕರಿಸಿದ ಮೊತ್ತ',
    accountsReceivedHint: 'Paid ಎಂದು ಗುರುತಿಸಿದ ಇನ್ವಾಯ್ಸ್‌ಗಳಿಂದ ಬಂದ ಹಣ. History-ಯಲ್ಲಿ ಸ್ಥಿತಿ ಅಪ್‌ಡೇಟ್ ಮಾಡಿ.',
    collectionSnapshot: 'ವಸೂಲಿ ಸಾರಾಂಶ',
    collectionSnapshotCopy: 'ಇನ್ವಾಯ್ಸ್ ಚಟುವಟಿಕೆಯಿಂದ paid, pending ಮತ್ತು overdue ಮೊತ್ತ.',
    collectionSnapshotHint: 'ಕೆಂಪು ಬಣ್ಣದಲ್ಲಿ overdue sent ಇನ್ವಾಯ್ಸ್‌ಗಳು ಮತ್ತು ಕಳುಹಿಸುವ ಮೊದಲು ಪರಿಶೀಲಿಸಬೇಕಾದ past-due drafts.',
    details: 'ವಿವರಗಳು',
    collectionRate: 'ವಸೂಲಿ ದರ',
    invoiceValue: 'ಇನ್ವಾಯ್ಸ್ ಮೌಲ್ಯ',
    drafts: 'ಡ್ರಾಫ್ಟ್‌ಗಳು',
    pastDue: 'ಗಡುವು ಮೀರಿದೆ',
    received: 'ಸ್ವೀಕರಿಸಿದೆ',
    pending: 'ಬಾಕಿ',
    needsAttention: 'Overdue / ಗಮನ ಬೇಕು',
    unlimited: 'ಅನಿಯಮಿತ',
    monthly: 'ಮಾಸಿಕ',
    yearly: 'ವಾರ್ಷಿಕ',
    savePercent: '17% ಉಳಿಸಿ',
    billedMonthly: 'ಮಾಸಿಕ ಬಿಲ್ಲಿಂಗ್',
    billedYearly: 'ವಾರ್ಷಿಕ ಬಿಲ್ಲಿಂಗ್',
    cancelAnytime: 'ಯಾವಾಗ ಬೇಕಾದರೂ ರದ್ದು',
    starter: 'Starter',
    chooseStarter: 'Starter ಆಯ್ಕೆ',
    openingRazorpay: 'Razorpay ತೆರೆಯುತ್ತಿದೆ...',
    checkoutFailed: 'Checkout ಪ್ರಾರಂಭಿಸಲು ಆಗಲಿಲ್ಲ. ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    starterActive: 'Starter ಈಗಾಗಲೇ ಸಕ್ರಿಯವಾಗಿದೆ.',
    activated: 'ಸಕ್ರಿಯವಾಗಿದೆ',
    manualWhatsappShare: 'Manual WhatsApp ಹಂಚಿಕೆ',
    accountInvoiceHistory: 'ಖಾತೆ ಇನ್ವಾಯ್ಸ್ ಇತಿಹಾಸ',
    businessProfileAutofill: 'ವ್ಯವಹಾರ ಪ್ರೊಫೈಲ್ ಮತ್ತು GST autofill',
    recurringMandate: 'ರದ್ದು ಮಾಡುವವರೆಗೆ recurring mandate',
    comingSoon: 'ಶೀಘ್ರದಲ್ಲೇ',
    whatLaunchesNext: 'ಮುಂದೆ ಬರುವವು',
    julyOnward: 'ಜುಲೈ ನಂತರ',
    basicPlanSoon: 'Basic ಪ್ಲಾನ್',
    basicPlanSoonCopy: 'ಅನಿಯಮಿತ ಇನ್ವಾಯ್ಸ್‌ಗಳು, ledger, GST estimator, profit calculator, outstanding tracker ಮತ್ತು CA exports.',
    razorpayIntegration: 'Razorpay ಏಕೀಕರಣ',
    razorpayIntegrationCopy: 'ಪಾವತಿಗಳನ್ನು ಆಮದು ಮಾಡಿ, ಇನ್ವಾಯ್ಸ್‌ಗಳಿಗೆ ಹೊಂದಿಸಿ, paid/unpaid ಸ್ಥಿತಿ ಟ್ರ್ಯಾಕ್ ಮಾಡಿ.',
    metaIntegration: 'Meta ಏಕೀಕರಣ',
    metaIntegrationCopy: 'Seller billing ಮತ್ತು reconciliation ಗಾಗಿ Meta commerce/ad workflows ಜೋಡಿಸಿ.',
    shopifyImport: 'Shopify ಆಮದು',
    shopifyImportCopy: 'Store sales ನಿಂದ orders ಆಮದು ಮಾಡಿ GST ಇನ್ವಾಯ್ಸ್ ರಚಿಸಿ.',
    whatsappAutomation: 'WhatsApp automation',
    whatsappAutomationCopy: 'WhatsApp ಮೂಲಕ invoice links, due-date nudges, payment reminders ಕಳುಹಿಸಿ.',
    codRtoTracker: 'COD/RTO tracker',
    codRtoTrackerCopy: 'Cash-on-delivery, failed delivery ಮತ್ತು return-to-origin adjustments ಟ್ರ್ಯಾಕ್ ಮಾಡಿ.',
    returnsRefunds: 'Returns ಮತ್ತು refunds',
    returnsRefundsCopy: 'Invoice history ಕೆಡಿಸದೆ return, refund, credit-note adjustments ನಿರ್ವಹಿಸಿ.',
    sevenYearStorage: '7 ವರ್ಷ storage',
    sevenYearStorageCopy: 'Tax records ಗಾಗಿ ದೀರ್ಘಕಾಲಿನ invoice ಮತ್ತು document retention.',
    accountEmail: 'ಖಾತೆ ಇಮೇಲ್',
    businessEmail: 'ವ್ಯವಹಾರ ಇಮೇಲ್',
    profileSaved: 'ಪ್ರೊಫೈಲ್ ಉಳಿಸಲಾಗಿದೆ',
    profileSaveFailed: 'ಪ್ರೊಫೈಲ್ ಉಳಿಸಲು ವಿಫಲ',
    savedOnBrowser: 'ಈ browser ನಲ್ಲಿ ಉಳಿಸಲಾಗಿದೆ',
    sealSignatureSizeError: 'ಸೀಲ್/ಸಹಿ ಚಿತ್ರ 2MB ಕ್ಕಿಂತ ಕಡಿಮೆ ಇರಬೇಕು',
    sealSignatureAlt: 'ಸೀಲ್ ಮತ್ತು ಸಹಿ',
    sealSignatureHelp: 'ಸಾಧ್ಯವಾದರೆ transparent PNG ಬಳಸಿ. ಚಿತ್ರ invoice preview ಮತ್ತು downloaded PDF ಯಲ್ಲಿ authorised signature ಭಾಗದ ಬಳಿ ಕಾಣುತ್ತದೆ.',
    wantCloudSync: 'Cloud sync ಬೇಕೇ?',
    cloudSyncCopy: 'ಹೆಚ್ಚಿನ ಮಾಸಿಕ ಇನ್ವಾಯ್ಸ್‌ಗಳು ಮತ್ತು ಉಳಿಸಿದ ವ್ಯವಹಾರ ವಿವರಗಳಿಗೆ Starter ಗೆ ಅಪ್‌ಗ್ರೇಡ್ ಮಾಡಿ.',
    viewPlan: 'ಪ್ಲಾನ್ ನೋಡಿ',
    superadmin: 'Superadmin',
    permanent: 'ಶಾಶ್ವತ',
    sevenDays: '7 ದಿನ',
    fourteenDays: '14 ದಿನ',
    thirtyDays: '30 ದಿನ',
    ninetyDays: '90 ದಿನ',
    superadminPlanOverride: 'Superadmin plan override',
    planOverrideApplied: 'Plan override ಅನ್ವಯಿಸಲಾಗಿದೆ',
    planOverrideCleared: 'Plan override ತೆರವುಗೊಳಿಸಲಾಗಿದೆ',
    saveInvoicesAccount: 'ಇನ್ವಾಯ್ಸ್‌ಗಳನ್ನು ನಿಮ್ಮ ಖಾತೆಗೆ ಉಳಿಸಿ',
    saveInvoicesAccountCopy: 'Free ಖಾತೆಗೆ ತಿಂಗಳಿಗೆ 5 watermarked invoices ಮತ್ತು history ಸಿಗುತ್ತದೆ. Starter 100 invoices ಮತ್ತು saved business details ಕೊಡುತ್ತದೆ.',
    invoicesSavedAfterLogin: 'ಲಾಗಿನ್ ನಂತರ ನಿಮ್ಮ ಇನ್ವಾಯ್ಸ್‌ಗಳು ಉಳಿಯುತ್ತವೆ',
    invoicesSavedAfterLoginCopy: 'Invoice history ಗಾಗಿ free account ರಚಿಸಿ. Starter ಹೆಚ್ಚು monthly invoices ಮತ್ತು GST profile autofill ನೀಡುತ್ತದೆ.',
    upgradeInvoiceLimit: 'ಇನ್ವಾಯ್ಸ್ ಮಿತಿ ಅಪ್‌ಗ್ರೇಡ್ ಮಾಡಿ',
    upgradeInvoiceLimitCopy: 'Starter 100 monthly invoices, account history ಮತ್ತು saved GST business details ನೀಡುತ್ತದೆ.',
    invoicesEverywhere: 'ನಿಮ್ಮ ಇನ್ವಾಯ್ಸ್‌ಗಳು, ಎಲ್ಲೆಡೆ',
    invoicesEverywhereCopy: 'Starter ಹೆಚ್ಚು monthly invoices, account history ಮತ್ತು saved GST business details ನೀಡುತ್ತದೆ.',
    cloudSync: 'Cloud Sync',
    invoiceHistoryFeature: 'Invoice History',
    hundredInvoices: '100 Invoices',
    notNow: 'ಈಗ ಬೇಡ',
    seePlan: 'ಪ್ಲಾನ್ ನೋಡಿ',
    homeHeroKicker: 'ಭಾರತೀಯ sellers ಗಾಗಿ GST invoices',
    homeHeroTitle: 'ಪ್ರತಿ GST invoice ಗೆ spreadsheet ಕಷ್ಟ ಬೇಡ.',
    homeHeroCopy: 'BillJaldi ಸ್ವಚ್ಛ GST invoices ರಚಿಸಲು, WhatsApp ನಲ್ಲಿ ಹಂಚಲು, buyer ಅಥವಾ CA ಕೇಳಿದಾಗ customer records ಸಿದ್ಧವಾಗಿಡಲು ಸಹಾಯ ಮಾಡುತ್ತದೆ.',
    startFree: 'Free ಪ್ರಾರಂಭಿಸಿ',
    painTitle: 'Billing ನ ಕಷ್ಟ PDF ಮಾತ್ರ ಅಲ್ಲ.',
    painCopy: 'Invoice numbers ನೆನಪಿಡುವುದು, GST split ಸರಿಪಡಿಸುವುದು, ಹಳೆಯ customer details ಹುಡುಕುವುದು, payment due ಆಗಿದಾಗ ಏನು ಕಳುಹಿಸಿದ್ದೆವು ತೋರಿಸುವುದು ಕೆಲಸ.',
    painOldInvoices: 'ಹಳೆಯ invoices chats ಮತ್ತು downloads ನಲ್ಲಿ ಕಳೆದುಹೋಗುತ್ತವೆ.',
    painTaxSplit: 'CGST, SGST, IGST checks ಸಮಯ ತೆಗೆದುಕೊಳ್ಳುತ್ತವೆ.',
    painFollowup: 'Payment follow-ups WhatsApp ನಲ್ಲಿ manual ಆಗಿರುತ್ತವೆ.',
    painRecords: 'Month end ನಲ್ಲಿ CA-ready records ಚದುರಿರುತ್ತವೆ.',
    howHelps: 'BillJaldi ಹೇಗೆ ಸಹಾಯ ಮಾಡುತ್ತದೆ',
    helpFastPdf: 'Seconds ನಲ್ಲಿ GST invoice PDF ರಚಿಸಿ',
    helpAutoTax: 'CGST, SGST, IGST auto-calculate',
    helpShare: 'Due date text ಜೊತೆಗೆ WhatsApp ಅಥವಾ email share',
    helpHistory: 'Login ನಂತರ invoice history save',
    helpCustomersItems: 'Saved customers ಮತ್ತು items reuse',
    helpSeal: 'Profile ನಿಂದ seal/signature ಸೇರಿಸಿ',
    builtFor: 'ಯಾರಿಗಾಗಿ',
    builtFreelancers: 'Freelancers',
    builtShops: 'ಸಣ್ಣ ಅಂಗಡಿಗಳು',
    builtInstagram: 'Instagram sellers',
    builtServices: 'Service providers',
    builtD2c: 'D2C operators',
    homePlanCopy: 'Free: 5 invoices/month. Starter ₹249/month. Regular price ₹399.',
    legal: 'Legal',
    privacy: 'Privacy',
    changelog: 'Changelog',
    home: 'Home',
    versionHistory: 'Version history',
    versionHistoryCopy: 'ಏನು shipped ಆಯಿತು ಮತ್ತು ಮುಂದೆ ಏನು ಬರುತ್ತಿದೆ ಎಂಬ simple record.',
    currentVersion: 'Current version',
    upcoming: 'Upcoming',
    upcomingCopy: 'v1.1 ನಂತರ planned product tracks.',
    ledgerFeature: 'Ledger',
    ledgerFeatureCopy: 'Customer-wise receivables, paid/unpaid status, account-level views.',
    v10Title: 'v1.0 public launch',
    v10Copy: 'Google sign-in, invoice creation, PDF download, saved history, customers, items, dashboard, Starter payments, admin controls.',
    v11Title: 'v1.1 in progress',
    v11Copy: 'Language support, new landing page, changelog, seal/stamp support, better mobile navigation.',
    hidePreview: 'ಮರೆಮಾಡಿ',
    invoiceLimitCopy: 'ಈ ತಿಂಗಳ invoice limit ಮುಗಿದಿದೆ. ಇನ್ನಷ್ಟು invoices ರಚಿಸಲು upgrade ಮಾಡಿ.',
    loggedOut: 'ಲಾಗ್ ಔಟ್ ಆಗಿದೆ',
    signingOut: 'ಲಾಗ್ ಔಟ್ ಆಗುತ್ತಿದೆ',
    logoutDoneCopy: 'ಈ device clean ಆಗಿದೆ. Invoice drafts, local history, customers ಮತ್ತು items clear ಮಾಡಲಾಗಿದೆ.',
    logoutProgressCopy: 'ಈ device clear ಆಗುತ್ತಿದೆ ಮತ್ತು workspace securely ಮುಚ್ಚುತ್ತಿದೆ.',
    builtMadeIndia: 'ಭಾರತದಲ್ಲಿ ನಿರ್ಮಿಸಲಾಗಿದೆ',
    upgrade: 'ಅಪ್‌ಗ್ರೇಡ್',
    free: 'Free',
    expandSidebar: 'ಸೈಡ್‌ಬಾರ್ ವಿಸ್ತರಿಸಿ',
    collapseSidebar: 'ಸೈಡ್‌ಬಾರ್ ಮುಚ್ಚಿ',
    businessProfile: 'ವ್ಯವಹಾರ ಪ್ರೊಫೈಲ್',
    previewFailed: 'ಪೂರ್ವವೀಕ್ಷಣೆ ವಿಫಲ',
    duplicateLimitReached: 'ಈ ತಿಂಗಳ ಇನ್ವಾಯ್ಸ್ ಮಿತಿ ಮುಗಿದಿದೆ. ನಕಲಿಸಲು ಅಪ್‌ಗ್ರೇಡ್ ಮಾಡಿ.',
    invoiceCopied: 'ಇನ್ವಾಯ್ಸ್ ನಕಲಿಸಲಾಗಿದೆ. ಪರಿಶೀಲಿಸಿ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ.',
    pdfCloudStorageFailed: 'ಇನ್ವಾಯ್ಸ್ ಉಳಿಸಲಾಗಿದೆ, ಆದರೆ PDF cloud storage ವಿಫಲವಾಗಿದೆ.',
    watermarkNotice: 'Free ಮತ್ತು Starter invoices ಗಳಲ್ಲಿ BillJaldi watermark ಇರುತ್ತದೆ.',
    loginSaveHistoryNotice: 'ಇನ್ವಾಯ್ಸ್ ಇತಿಹಾಸ ಉಳಿಸಲು ಲಾಗಿನ್ ಮಾಡಿ.',
    invoiceDownloaded: 'ಇನ್ವಾಯ್ಸ್ ಡೌನ್‌ಲೋಡ್ ಆಗಿದೆ!',
    pdfGenerateFailed: 'PDF ರಚಿಸಲು ಆಗಲಿಲ್ಲ. ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    invoiceDisclaimer: 'ರಚಿಸಲಾದ ಯಾವುದೇ ಇನ್ವಾಯ್ಸ್‌ನ accuracy, legality ಅಥವಾ tax compliance ಗೆ billjaldi ಹೊಣೆಗಾರನಲ್ಲ.',
    logoSizeError: 'ಲೋಗೋ 2MB ಕ್ಕಿಂತ ಕಡಿಮೆ ಇರಬೇಕು',
    loginRightHeadline: 'ನಿಮ್ಮ ಇನ್ವಾಯ್ಸ್‌ಗಳು, ಗ್ರಾಹಕರು ಮತ್ತು ಐಟಂಗಳು ನಿಮ್ಮ ಖಾತೆಗೆ ಜೋಡಿಸಿಕೊಂಡೇ ಇರುತ್ತವೆ.',
    loginRightCopy: 'ಸೈನ್ ಇನ್ ಆದ ನಂತರ BillJaldi ಖಾತೆಯ ಇನ್ವಾಯ್ಸ್‌ಗಳನ್ನು ಸ್ವಯಂಚಾಲಿತವಾಗಿ ಉಳಿಸುತ್ತದೆ. Manual cloud migration, ಪ್ರತ್ಯೇಕ password, duplicate signup path ಇಲ್ಲ.',
    hundredInvoicesMonth: '100 ಇನ್ವಾಯ್ಸ್/ತಿಂಗಳು',
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => localStorage.getItem('bj_language') || 'en');
  const value = useMemo(() => {
    const safeLanguage = COPY[language] ? language : 'en';
    return {
      language: safeLanguage,
      setLanguage: (next) => {
        const safeNext = COPY[next] ? next : 'en';
        localStorage.setItem('bj_language', safeNext);
        setLanguage(safeNext);
      },
      t: (key) => COPY[safeLanguage]?.[key] || COPY.en[key] || key,
    };
  }, [language]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be inside LanguageProvider');
  return ctx;
}
